import {
  AUTHORED_PEDESTRIAN_STREET_WIDTH_PROFILES,
  AUTHORED_STREET_EARTHWORK_PROFILES,
  AUTHORED_STREET_GRADE_PROFILES,
  NO_PROCEDURAL_SIDEWALK_FALLBACK_SOURCE_IDS,
  STREET_SURFACE_CONFIG,
  type AuthoredStreetEarthworkProfile,
  type AuthoredStreetGradeProfile,
  type StreetSurfaceFamily,
  type StreetSurfaceFamilyConfig,
} from "../config/streets.ts";
import { isWithinOldTownDevelopmentAoi } from "./buildings/index.ts";
import type { LocalHorizontal } from "./coordinates.ts";
import type { RuntimeLinearFeature } from "./schema/runtimeTile.ts";
import type { TerrainTileData } from "./schema/tile.ts";
import { sampleTerrainTileData } from "./terrain.ts";

export interface StreetMeshData {
  readonly positions: readonly number[];
  readonly uvs: readonly number[];
  readonly indices: readonly number[];
}

export const ROAD_SURFACE_CLASSES = [
  "default",
  "asphalt",
  "cement_concrete",
  "paving",
  "aggregate",
  "gravel",
  "wood",
  "other",
] as const;

export type RoadSurfaceClass = (typeof ROAD_SURFACE_CLASSES)[number];

export const PEDESTRIAN_SURFACE_CLASSES = [
  "default",
  "cycleway",
  "asphalt",
  "concrete",
  "paving",
  "sand",
  "wood",
  "other",
] as const;

export type PedestrianSurfaceClass =
  (typeof PEDESTRIAN_SURFACE_CLASSES)[number];

export interface StreetSurfaceMeshSet {
  readonly family: StreetSurfaceFamily;
  readonly carriageway: StreetMeshData | null;
  /** Full-detail carriageway geometry grouped by official GRPK DANGA. */
  readonly carriagewaySurfaces: Readonly<
    Record<RoadSurfaceClass, StreetMeshData | null>
  >;
  readonly shoulders: StreetMeshData | null;
  /** Exposed fascia and underside for source-verified elevated road decks. */
  readonly elevatedDecks: StreetMeshData | null;
  readonly junctions: StreetMeshData | null;
  /** Full-detail junction geometry assigned to its dominant incoming surface. */
  readonly junctionSurfaces: Readonly<
    Record<RoadSurfaceClass, StreetMeshData | null>
  >;
  readonly sidewalks: StreetMeshData | null;
  readonly curbs: StreetMeshData | null;
  readonly pedestrian: StreetMeshData | null;
  /** Full-detail mapped path geometry grouped by OSM surface evidence. */
  readonly pedestrianSurfaces: Readonly<
    Record<PedestrianSurfaceClass, StreetMeshData | null>
  >;
  readonly carriagewayFeatureIds: readonly string[];
  readonly pedestrianFeatureIds: readonly string[];
  readonly junctionCount: number;
}

export interface StreetSurfaceMeshOptions {
  /**
   * Reduced visual LODs can follow their coarser terrain sampling without
   * retaining the six-metre subdivision used by nearby collision and paving.
   */
  readonly maximumSegmentLengthM?: number;
  /** Owning tile origin used by world-space authored grade profiles. */
  readonly tileOrigin?: Readonly<LocalHorizontal>;
}

export interface PedestrianRoutePolyline {
  readonly side: "centre" | "left" | "right";
  readonly points: readonly LocalHorizontal[];
}

interface MutableStreetMeshData {
  positions: number[];
  uvs: number[];
  indices: number[];
}

interface SampledStreetPoint extends LocalHorizontal {
  readonly distanceM: number;
}

interface StreetCrossSection {
  readonly point: SampledStreetPoint;
  readonly normalX: number;
  readonly normalZ: number;
}

interface StreetFeatureTrim {
  startM: number;
  endM: number;
}

interface PedestrianRoadTransition {
  readonly start: boolean;
  readonly end: boolean;
}

interface StreetJunctionEndpoint {
  readonly feature: Readonly<RuntimeLinearFeature>;
  readonly endpoint: "start" | "end";
  readonly point: Readonly<LocalHorizontal>;
  readonly gradeLayer: string;
}

interface StreetJunctionCandidateLeg {
  readonly endpoint: Readonly<StreetJunctionEndpoint>;
  readonly directionX: number;
  readonly directionZ: number;
  readonly normalX: number;
  readonly normalZ: number;
  readonly leftOuterHalfWidthM: number;
  readonly rightOuterHalfWidthM: number;
  readonly angleRadians: number;
}

interface StreetJunctionLeg {
  readonly featureId: string;
  readonly surfaceClass: RoadSurfaceClass;
  readonly centre: Readonly<LocalHorizontal>;
  readonly directionX: number;
  readonly directionZ: number;
  readonly normalX: number;
  readonly normalZ: number;
  readonly halfWidthM: number;
  readonly curbWidthM: number;
  readonly sidewalkWidthM: number;
  readonly sidewalkLeft: boolean;
  readonly sidewalkRight: boolean;
  readonly angleRadians: number;
}

interface StreetJunction {
  readonly point: Readonly<LocalHorizontal>;
  readonly cutbackM: number;
  readonly gradeLayer: string;
  readonly legs: readonly StreetJunctionLeg[];
}

interface StreetJunctionTopology {
  readonly junctions: readonly StreetJunction[];
  readonly trimsByFeatureId: ReadonlyMap<string, Readonly<StreetFeatureTrim>>;
}

interface JunctionCorner {
  readonly inner: readonly LocalHorizontal[];
  readonly curbOuter: readonly LocalHorizontal[];
  readonly sidewalkOuter: readonly LocalHorizontal[];
  readonly hasSidewalk: boolean;
}

interface CarriagewayCorridorSegment {
  readonly featureId: string;
  readonly featureName: string | null;
  readonly from: LocalHorizontal;
  readonly to: LocalHorizontal;
  readonly halfWidthM: number;
  readonly gradeProfileId: string | null;
}

type StreetSegmentFilter = (
  from: Readonly<StreetCrossSection>,
  to: Readonly<StreetCrossSection>,
  minimumOffsetM: number,
  maximumOffsetM: number,
) => boolean;

const EMPTY_MESH = (): MutableStreetMeshData => ({
  positions: [],
  uvs: [],
  indices: [],
});

function emptyRoadSurfaceMeshes(): Record<
  RoadSurfaceClass,
  MutableStreetMeshData
> {
  return Object.fromEntries(
    ROAD_SURFACE_CLASSES.map((surfaceClass) => [surfaceClass, EMPTY_MESH()]),
  ) as Record<RoadSurfaceClass, MutableStreetMeshData>;
}

function emptyPedestrianSurfaceMeshes(): Record<
  PedestrianSurfaceClass,
  MutableStreetMeshData
> {
  return Object.fromEntries(
    PEDESTRIAN_SURFACE_CLASSES.map((surfaceClass) => [
      surfaceClass,
      EMPTY_MESH(),
    ]),
  ) as Record<PedestrianSurfaceClass, MutableStreetMeshData>;
}

export function streetFeatureRoadSurfaceClass(
  feature: Readonly<RuntimeLinearFeature>,
): RoadSurfaceClass {
  switch (feature.surfaceCode) {
    case "A":
      return "asphalt";
    case "C":
      return "cement_concrete";
    case "G":
      return "paving";
    case "Sk":
      return "aggregate";
    case "Ž":
      return "gravel";
    case "Md":
      return "wood";
    case "KT":
      return "other";
    case undefined:
      return "default";
    default:
      return "other";
  }
}

export function streetFeaturePedestrianSurfaceClass(
  feature: Readonly<RuntimeLinearFeature>,
): PedestrianSurfaceClass {
  const sourceFeatureId = feature.id
    .split("#", 1)[0]
    ?.toLocaleLowerCase("lt-LT");
  const sourceProfile = sourceFeatureId === undefined
    ? undefined
    : STREET_SURFACE_CONFIG.pedestrianSourceProfiles[sourceFeatureId];
  if (sourceProfile?.surfaceClass === "cycleway") {
    return "cycleway";
  }
  const classCode = feature.classCode?.trim().toLowerCase();
  if (classCode?.startsWith("cycleway:") === true) {
    return "cycleway";
  }
  const surfacePrefix = classCode?.startsWith("sidewalk:") === true
    ? "sidewalk:"
    : classCode?.startsWith("footway:") === true
    ? "footway:"
    : undefined;
  if (classCode === undefined || surfacePrefix === undefined) {
    return "default";
  }
  const surface = classCode.slice(surfacePrefix.length);
  switch (surface) {
    case "asphalt":
      return "asphalt";
    case "concrete":
    case "concrete:plates":
      return "concrete";
    case "paved":
    case "paving_stones":
      return "paving";
    case "sand":
      return "sand";
    case "wood":
      return "wood";
    case "unknown":
    case "":
      return "default";
    default:
      return "other";
  }
}

export function streetSurfaceFamilyForTile(
  tileOrigin: Readonly<LocalHorizontal>,
  tileSizeM: number,
): StreetSurfaceFamily {
  const centre = {
    x: tileOrigin.x + tileSizeM / 2,
    z: tileOrigin.z + tileSizeM / 2,
  };
  return isWithinOldTownDevelopmentAoi(centre) ? "old_town" : "general";
}

export function isPedestrianStreetFeature(
  feature: Readonly<RuntimeLinearFeature>,
): boolean {
  return feature.kind === "path" || feature.classCode.toLowerCase() === "dc2";
}

export function isFullWidthPedestrianStreetFeature(
  feature: Readonly<RuntimeLinearFeature>,
): boolean {
  return isPedestrianStreetFeature(feature) &&
    feature.pedestrianStreetFullWidth === true;
}

export function streetFeatureWidthM(
  feature: Readonly<RuntimeLinearFeature>,
): number {
  if (isPedestrianStreetFeature(feature)) {
    if (feature.widthM !== undefined) {
      return feature.widthM;
    }
    const sourceFeatureId = feature.id
      .split("#", 1)[0]
      ?.toLocaleLowerCase("lt-LT");
    const sourceWidthM = (
      sourceFeatureId === undefined
        ? undefined
        : STREET_SURFACE_CONFIG.pedestrianSourceProfiles[sourceFeatureId]
    )?.widthM;
    if (sourceWidthM !== undefined) {
      return sourceWidthM;
    }
    return feature.classCode.trim().toLowerCase().startsWith("cycleway:")
      ? STREET_SURFACE_CONFIG.cyclewayWidthM
      : STREET_SURFACE_CONFIG.pedestrianWidthM;
  }
  if (feature.carriagewayWidthM !== undefined) {
    return feature.carriagewayWidthM;
  }
  if (feature.totalWidthM !== undefined) {
    return feature.totalWidthM;
  }
  return feature.classCode.toLowerCase() === "gc12"
    ? STREET_SURFACE_CONFIG.wideCarriagewayWidthM
    : STREET_SURFACE_CONFIG.defaultCarriagewayWidthM;
}

export function streetFeatureWidthAtPointM(
  feature: Readonly<RuntimeLinearFeature>,
  point: Readonly<LocalHorizontal>,
  tileOrigin: Readonly<LocalHorizontal>,
): number {
  const fallbackWidthM = streetFeatureWidthM(feature);
  if (!isPedestrianStreetFeature(feature)) {
    return fallbackWidthM;
  }
  const sourceFeatureId = streetSourceFeatureId(feature);
  const profile = AUTHORED_PEDESTRIAN_STREET_WIDTH_PROFILES.find(
    (candidate) => candidate.sourceFeatureId === sourceFeatureId,
  );
  if (profile === undefined || profile.anchors.length < 2) {
    return fallbackWidthM;
  }
  const worldPoint = {
    x: tileOrigin.x + point.x,
    z: tileOrigin.z + point.z,
  };
  let nearestDistanceM = Number.POSITIVE_INFINITY;
  let authoredWidthM = fallbackWidthM;
  for (let index = 1; index < profile.anchors.length; index += 1) {
    const from = profile.anchors[index - 1];
    const to = profile.anchors[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthSquaredM = deltaX * deltaX + deltaZ * deltaZ;
    const t = lengthSquaredM <= 1e-8
      ? 0
      : Math.max(
        0,
        Math.min(
          1,
          (
            (worldPoint.x - from.x) * deltaX +
            (worldPoint.z - from.z) * deltaZ
          ) / lengthSquaredM,
        ),
      );
    const projectedX = from.x + deltaX * t;
    const projectedZ = from.z + deltaZ * t;
    const distanceM = Math.hypot(
      worldPoint.x - projectedX,
      worldPoint.z - projectedZ,
    );
    if (distanceM < nearestDistanceM) {
      nearestDistanceM = distanceM;
      authoredWidthM = from.widthM + (to.widthM - from.widthM) * t;
    }
  }
  return authoredWidthM;
}

/**
 * Returns the official outer road envelope when GRPK provides both JPLOTIS
 * (carriageway) and PLOTIS (the wider road platform). Keeping this separate
 * from `streetFeatureWidthM` lets the driven surface retain its measured
 * width while shoulders, curbs, sidewalks and roadside props start at the
 * actual road edge.
 */
export function streetFeatureRoadEdgeWidthM(
  feature: Readonly<RuntimeLinearFeature>,
): number {
  const carriagewayWidthM = streetFeatureWidthM(feature);
  if (isPedestrianStreetFeature(feature)) {
    return carriagewayWidthM;
  }
  return Math.max(carriagewayWidthM, feature.totalWidthM ?? 0);
}

export function streetSurfaceConfigForFeature(
  feature: Readonly<RuntimeLinearFeature>,
  family: StreetSurfaceFamily,
): Readonly<StreetSurfaceFamilyConfig> {
  const base = STREET_SURFACE_CONFIG.families[family];
  const name = feature.name?.trim().toLocaleLowerCase("lt-LT");
  const named = name === undefined
    ? undefined
    : STREET_SURFACE_CONFIG.namedStreetProfiles[name];
  let sidewalkWidthM = named?.sidewalkWidthM ?? base.sidewalkWidthM;
  if (
    !isPedestrianStreetFeature(feature) &&
    feature.publicRealmWidthM !== undefined
  ) {
    const sidewalkSideCount = streetFeatureSidewalkSides(feature).length;
    if (sidewalkSideCount > 0) {
      const continuousSidewalkWidthM =
        (feature.publicRealmWidthM - streetFeatureRoadEdgeWidthM(feature)) /
          sidewalkSideCount -
        base.curbWidthM;
      sidewalkWidthM = Math.max(sidewalkWidthM, continuousSidewalkWidthM);
    }
  }
  return sidewalkWidthM === base.sidewalkWidthM
    ? base
    : { ...base, sidewalkWidthM };
}

export function streetFeatureSidewalkWidthM(
  feature: Readonly<RuntimeLinearFeature>,
  family: StreetSurfaceFamily,
): number {
  return streetSurfaceConfigForFeature(feature, family).sidewalkWidthM;
}

function pedestrianRoadTransitionForFeature(
  feature: Readonly<RuntimeLinearFeature>,
  features: readonly Readonly<RuntimeLinearFeature>[],
): Readonly<PedestrianRoadTransition> | null {
  if (
    isPedestrianStreetFeature(feature) ||
    feature.publicRealmWidthM === undefined
  ) {
    return null;
  }
  const name = normalizedStreetName(feature.name);
  const first = feature.points[0];
  const last = feature.points[feature.points.length - 1];
  if (name === null || first === undefined || last === undefined) {
    return null;
  }
  let start = false;
  let end = false;
  const toleranceM =
    STREET_SURFACE_CONFIG.pedestrianTransitionEndpointToleranceM;
  for (const candidate of features) {
    if (
      !isFullWidthPedestrianStreetFeature(candidate) ||
      normalizedStreetName(candidate.name) !== name
    ) {
      continue;
    }
    const candidateFirst = candidate.points[0];
    const candidateLast = candidate.points[candidate.points.length - 1];
    for (const endpoint of [candidateFirst, candidateLast]) {
      if (endpoint === undefined) {
        continue;
      }
      start ||= Math.hypot(first.x - endpoint.x, first.z - endpoint.z) <=
        toleranceM;
      end ||= Math.hypot(last.x - endpoint.x, last.z - endpoint.z) <=
        toleranceM;
    }
  }
  return start || end ? { start, end } : null;
}

function normalizedStreetName(name: string | undefined): string | null {
  const normalized = name?.trim().toLocaleLowerCase("lt-LT");
  return normalized ? normalized : null;
}

function pedestrianTransitionHeightBlend(
  sections: readonly StreetCrossSection[],
  transition: Readonly<PedestrianRoadTransition>,
): readonly number[] {
  const lastDistanceM = sections.at(-1)?.point.distanceM ?? 0;
  const firstDistanceM = sections[0]?.point.distanceM ?? 0;
  const totalLengthM = Math.max(0, lastDistanceM - firstDistanceM);
  const endpointCount = Number(transition.start) + Number(transition.end);
  const transitionLengthM = Math.min(
    STREET_SURFACE_CONFIG.pedestrianTransitionLengthM,
    totalLengthM / Math.max(1, endpointCount),
  );
  if (transitionLengthM <= 0.01) {
    return sections.map(() => 1);
  }
  return sections.map(({ point }) => {
    let linearBlend = 1;
    if (transition.start) {
      linearBlend = Math.min(
        linearBlend,
        (point.distanceM - firstDistanceM) / transitionLengthM,
      );
    }
    if (transition.end) {
      linearBlend = Math.min(
        linearBlend,
        (lastDistanceM - point.distanceM) / transitionLengthM,
      );
    }
    const clamped = Math.max(0, Math.min(1, linearBlend));
    return clamped * clamped * (3 - 2 * clamped);
  });
}

function pedestrianGatewayMaximumDepthM(
  sections: readonly StreetCrossSection[],
  transition: Readonly<PedestrianRoadTransition>,
): number {
  const firstDistanceM = sections[0]?.point.distanceM ?? 0;
  const lastDistanceM = sections.at(-1)?.point.distanceM ?? firstDistanceM;
  const endpointCount = Number(transition.start) + Number(transition.end);
  return Math.min(
    STREET_SURFACE_CONFIG.pedestrianGatewayCurveDepthM,
    Math.max(0, lastDistanceM - firstDistanceM) /
      Math.max(1, endpointCount),
  );
}

function streetSourceFeatureId(
  feature: Readonly<RuntimeLinearFeature>,
): string | undefined {
  return feature.id.split("#", 1)[0]?.toLowerCase();
}

function streetGradeProfileForFeature(
  feature: Readonly<RuntimeLinearFeature>,
): Readonly<AuthoredStreetGradeProfile> | null {
  const sourceFeatureId = streetSourceFeatureId(feature);
  if (sourceFeatureId === undefined) {
    return null;
  }
  return AUTHORED_STREET_GRADE_PROFILES.find((profile) =>
    profile.sourceFeatureIds.has(sourceFeatureId)
  ) ?? null;
}

function authoredStreetElevationM(
  profile: Readonly<AuthoredStreetGradeProfile>,
  worldPoint: Readonly<LocalHorizontal>,
): number | null {
  const anchors = profile.anchors;
  if (anchors !== undefined && anchors.length >= 2) {
    let nearestDistanceM = Number.POSITIVE_INFINITY;
    let nearestElevationM = anchors[0]?.elevationM ?? profile.start.elevationM;
    let nearestSegmentIndex = 0;
    let nearestUnclampedT = 0;
    for (let index = 1; index < anchors.length; index += 1) {
      const from = anchors[index - 1];
      const to = anchors[index];
      if (from === undefined || to === undefined) {
        continue;
      }
      const deltaX = to.x - from.x;
      const deltaZ = to.z - from.z;
      const lengthSquaredM = deltaX * deltaX + deltaZ * deltaZ;
      const unclampedT = lengthSquaredM <= 1e-8
        ? 0
        : (
          (worldPoint.x - from.x) * deltaX +
          (worldPoint.z - from.z) * deltaZ
        ) / lengthSquaredM;
      const t = Math.max(0, Math.min(1, unclampedT));
      const closestX = from.x + deltaX * t;
      const closestZ = from.z + deltaZ * t;
      const distanceM = Math.hypot(
        worldPoint.x - closestX,
        worldPoint.z - closestZ,
      );
      if (distanceM >= nearestDistanceM) {
        continue;
      }
      nearestDistanceM = distanceM;
      nearestElevationM = from.elevationM +
        (to.elevationM - from.elevationM) * t;
      nearestSegmentIndex = index - 1;
      nearestUnclampedT = unclampedT;
    }
    if (
      profile.clampOutsideRange === false &&
      (
        (nearestSegmentIndex === 0 && nearestUnclampedT < 0) ||
        (
          nearestSegmentIndex === anchors.length - 2 &&
          nearestUnclampedT > 1
        )
      )
    ) {
      return null;
    }
    return nearestElevationM;
  }
  const deltaX = profile.end.x - profile.start.x;
  const deltaZ = profile.end.z - profile.start.z;
  const lengthSquaredM = deltaX * deltaX + deltaZ * deltaZ;
  const t = lengthSquaredM <= 1e-8
    ? 0
    : Math.max(
      0,
      Math.min(
        1,
        (
          (worldPoint.x - profile.start.x) * deltaX +
          (worldPoint.z - profile.start.z) * deltaZ
        ) / lengthSquaredM,
      ),
    );
  return profile.start.elevationM +
    (profile.end.elevationM - profile.start.elevationM) * t;
}

function earthworkCutAtPointM(
  profile: Readonly<AuthoredStreetEarthworkProfile>,
  worldPoint: Readonly<LocalHorizontal>,
): number {
  let nearestDistanceM = Number.POSITIVE_INFINITY;
  let nearestCutDepthM = 0;
  for (let index = 1; index < profile.anchors.length; index += 1) {
    const from = profile.anchors[index - 1];
    const to = profile.anchors[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthSquaredM = deltaX * deltaX + deltaZ * deltaZ;
    const t = lengthSquaredM <= 1e-8
      ? 0
      : Math.max(
        0,
        Math.min(
          1,
          (
            (worldPoint.x - from.x) * deltaX +
            (worldPoint.z - from.z) * deltaZ
          ) / lengthSquaredM,
        ),
      );
    const closestX = from.x + deltaX * t;
    const closestZ = from.z + deltaZ * t;
    const distanceM = Math.hypot(
      worldPoint.x - closestX,
      worldPoint.z - closestZ,
    );
    if (distanceM >= nearestDistanceM) {
      continue;
    }
    nearestDistanceM = distanceM;
    nearestCutDepthM = from.cutDepthM +
      (to.cutDepthM - from.cutDepthM) * t;
  }
  if (nearestDistanceM > profile.coreHalfWidthM + profile.bankWidthM) {
    return 0;
  }
  if (nearestDistanceM <= profile.coreHalfWidthM) {
    return nearestCutDepthM;
  }
  const bankT = (
    nearestDistanceM - profile.coreHalfWidthM
  ) / profile.bankWidthM;
  const smoothBankT = bankT * bankT * (3 - 2 * bankT);
  return nearestCutDepthM * (1 - smoothBankT);
}

/**
 * Restores source-verified road cuts that the coarse city terrain cannot
 * encode on its own. The world-space calculation is deterministic at shared
 * tile borders, so independently streamed render and physics tiles agree.
 */
export function terrainWithAuthoredStreetEarthworks(
  terrain: Readonly<TerrainTileData>,
  tileOrigin: Readonly<LocalHorizontal>,
): Readonly<TerrainTileData> {
  let changed = false;
  const heightsM = terrain.heightsM.map((heightM, index) => {
    const sampleX = index % terrain.samplesPerSide;
    const sampleZ = Math.floor(index / terrain.samplesPerSide);
    const worldPoint = {
      x: tileOrigin.x + sampleX * terrain.spacingM,
      z: tileOrigin.z + sampleZ * terrain.spacingM,
    };
    const cutDepthM = AUTHORED_STREET_EARTHWORK_PROFILES.reduce(
      (maximum, profile) => Math.max(
        maximum,
        earthworkCutAtPointM(profile, worldPoint),
      ),
      0,
    );
    if (cutDepthM <= 1e-6) {
      return heightM;
    }
    changed = true;
    return heightM - cutDepthM;
  });
  return changed ? { ...terrain, heightsM } : terrain;
}

/** Grounding shared by street surfaces and furniture placed on those roads. */
export function streetFeatureSurfaceElevationM(
  feature: Readonly<RuntimeLinearFeature>,
  terrain: Readonly<TerrainTileData>,
  tileOrigin: Readonly<LocalHorizontal>,
  localPoint: Readonly<LocalHorizontal>,
): number | null {
  const profile = streetGradeProfileForFeature(feature);
  const terrainElevationM = sampleTerrainTileData(
    terrain,
    { x: 0, z: 0 },
    localPoint,
  );
  if (profile === null) {
    return terrainElevationM;
  }
  return authoredStreetElevationM(profile, {
      x: tileOrigin.x + localPoint.x,
      z: tileOrigin.z + localPoint.z,
    }) ?? terrainElevationM;
}

function terrainForStreetFeature(
  feature: Readonly<RuntimeLinearFeature>,
  terrain: Readonly<TerrainTileData>,
  tileOrigin: Readonly<LocalHorizontal>,
): Readonly<TerrainTileData> {
  const profile = streetGradeProfileForFeature(feature);
  if (profile === null) {
    return terrain;
  }
  return terrainForStreetGradeProfile(profile, terrain, tileOrigin);
}

function terrainForStreetGradeProfile(
  profile: Readonly<AuthoredStreetGradeProfile>,
  terrain: Readonly<TerrainTileData>,
  tileOrigin: Readonly<LocalHorizontal>,
): Readonly<TerrainTileData> {
  const heightsM = Array.from(
    { length: terrain.samplesPerSide * terrain.samplesPerSide },
    (_, index) => {
      const sampleX = index % terrain.samplesPerSide;
      const sampleZ = Math.floor(index / terrain.samplesPerSide);
      return authoredStreetElevationM(profile, {
        x: tileOrigin.x + sampleX * terrain.spacingM,
        z: tileOrigin.z + sampleZ * terrain.spacingM,
      }) ?? terrain.heightsM[index]!;
    },
  );
  return {
    samplesPerSide: terrain.samplesPerSide,
    spacingM: terrain.spacingM,
    heightsM,
  };
}

/**
 * Signed offsets from the feature centreline. A positive offset follows the
 * mathematical left side of the stored point order; a negative offset is the
 * right side. Untagged roads retain the established two-sided fallback.
 */
export function streetFeatureSidewalkSides(
  feature: Readonly<RuntimeLinearFeature>,
): readonly (-1 | 1)[] {
  if (isFullWidthPedestrianStreetFeature(feature)) {
    return [];
  }
  switch (feature.sidewalkMode) {
    case "left":
      return [1];
    case "right":
      return [-1];
    case "none":
    case "separate":
      return [];
    case "both":
      return [-1, 1];
    case undefined: {
      const sourceFeatureId = streetSourceFeatureId(feature);
      return sourceFeatureId !== undefined &&
          NO_PROCEDURAL_SIDEWALK_FALLBACK_SOURCE_IDS.has(sourceFeatureId)
        ? []
        : [-1, 1];
    }
    default:
      return [-1, 1];
  }
}

/**
 * Returns the same centre tracks used by pedestrians on rendered street
 * surfaces: dedicated paths retain their source centreline, while roads expose
 * one route through the middle of each procedural sidewalk.
 */
export function pedestrianRoutePolylinesForStreetFeature(
  feature: Readonly<RuntimeLinearFeature>,
  family: StreetSurfaceFamily,
): readonly PedestrianRoutePolyline[] {
  if (feature.kind !== "road" && feature.kind !== "path") {
    return [];
  }
  const sections = createCrossSections(feature.points);
  if (sections.length < 2) {
    return [];
  }
  const sourcePointKeys = new Set(
    feature.points.map((point) => `${point.x.toFixed(6)}:${point.z.toFixed(6)}`),
  );
  const routeSections = sections.filter(({ point }) =>
    sourcePointKeys.has(`${point.x.toFixed(6)}:${point.z.toFixed(6)}`)
  );
  if (routeSections.length < 2) {
    return [];
  }
  if (isPedestrianStreetFeature(feature)) {
    return [{
      side: "centre",
      points: routeSections.map(({ point }) => ({ x: point.x, z: point.z })),
    }];
  }
  const familyConfig = streetSurfaceConfigForFeature(feature, family);
  const sidewalkCentreOffsetM =
    streetFeatureRoadEdgeWidthM(feature) / 2 +
    familyConfig.curbWidthM +
    familyConfig.sidewalkWidthM / 2;
  return streetFeatureSidewalkSides(feature).map((side) => ({
    side: side > 0 ? "left" : "right",
    points: routeSections.map(({ point, normalX, normalZ }) => ({
      x: point.x + normalX * sidewalkCentreOffsetM * side,
      z: point.z + normalZ * sidewalkCentreOffsetM * side,
    })),
  }));
}

export function createStreetSurfaceMeshSet(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
  family: StreetSurfaceFamily,
  options: Readonly<StreetSurfaceMeshOptions> = {},
): StreetSurfaceMeshSet {
  const familyConfig = STREET_SURFACE_CONFIG.families[family];
  const maximumSegmentLengthM =
    options.maximumSegmentLengthM ??
    STREET_SURFACE_CONFIG.maximumSegmentLengthM;
  const tileOrigin = options.tileOrigin ?? { x: 0, z: 0 };
  if (
    !Number.isFinite(maximumSegmentLengthM) ||
    maximumSegmentLengthM <= 0
  ) {
    throw new Error("Street maximum segment length must be positive");
  }
  const carriageway = EMPTY_MESH();
  const carriagewaySurfaces = emptyRoadSurfaceMeshes();
  const shoulders = EMPTY_MESH();
  const elevatedDecks = EMPTY_MESH();
  const junctions = EMPTY_MESH();
  const junctionSurfaces = emptyRoadSurfaceMeshes();
  const sidewalks = EMPTY_MESH();
  const curbs = EMPTY_MESH();
  const pedestrian = EMPTY_MESH();
  const pedestrianSurfaces = emptyPedestrianSurfaceMeshes();
  const carriagewayFeatureIds: string[] = [];
  const pedestrianFeatureIds: string[] = [];
  const corridors = createCarriagewayCorridors(features);
  const junctionTopology = createStreetJunctionTopology(
    features,
    family,
    terrain,
    tileOrigin,
  );

  for (const feature of features) {
    if (feature.kind !== "road" && feature.kind !== "path") {
      continue;
    }
    const pedestrianTransition = pedestrianRoadTransitionForFeature(
      feature,
      features,
    );
    const featureMaximumSegmentLengthM = pedestrianTransition === null
      ? maximumSegmentLengthM
      : Math.min(
        maximumSegmentLengthM,
        STREET_SURFACE_CONFIG.pedestrianTransitionMaximumSegmentLengthM,
      );
    const fullSections = createCrossSections(
      feature.points,
      0,
      0,
      featureMaximumSegmentLengthM,
    );
    if (fullSections.length < 2) {
      continue;
    }
    const halfWidthM = streetFeatureWidthM(feature) / 2;
    const featureConfig = streetSurfaceConfigForFeature(feature, family);
    const featureTerrain = terrainForStreetFeature(
      feature,
      terrain,
      tileOrigin,
    );
    if (isPedestrianStreetFeature(feature)) {
      const segmentFilter = createIntersectionSegmentFilter(
        feature,
        corridors,
        featureConfig,
      );
      const halfWidthsM = fullSections.map((section) =>
        streetFeatureWidthAtPointM(feature, section.point, tileOrigin) / 2
      );
      appendVariableWidthTopStrip(
        pedestrian,
        fullSections,
        halfWidthsM,
        featureConfig.pedestrianLiftM,
        terrain,
        segmentFilter,
      );
      appendVariableWidthTopStrip(
        pedestrianSurfaces[streetFeaturePedestrianSurfaceClass(feature)],
        fullSections,
        halfWidthsM,
        featureConfig.pedestrianLiftM,
        terrain,
        segmentFilter,
      );
      pedestrianFeatureIds.push(feature.id);
      continue;
    }

    const roadEdgeHalfWidthM = streetFeatureRoadEdgeWidthM(feature) / 2;
    const trim = junctionTopology.trimsByFeatureId.get(feature.id);
    const edgeSections = createCrossSections(
      feature.points,
      trim?.startM ?? 0,
      trim?.endM ?? 0,
      featureMaximumSegmentLengthM,
    );
    if (edgeSections.length < 2) {
      carriagewayFeatureIds.push(feature.id);
      continue;
    }

    const gatewayMaximumDepthM = pedestrianTransition === null
      ? 0
      : pedestrianGatewayMaximumDepthM(fullSections, pedestrianTransition);
    const boundaryExtendedSections = createCrossSections(
      extendStreetPolylineAtTileBoundary(
        feature.points,
        terrain,
        roadEdgeHalfWidthM + STREET_SURFACE_CONFIG.junctionCornerClearanceM,
      ),
      0,
      0,
      featureMaximumSegmentLengthM,
    );
    const roadSections = pedestrianTransition === null ||
        gatewayMaximumDepthM <= 0.01
      ? boundaryExtendedSections
      : createCrossSections(
        feature.points,
        pedestrianTransition.start ? gatewayMaximumDepthM : 0,
        pedestrianTransition.end ? gatewayMaximumDepthM : 0,
        featureMaximumSegmentLengthM,
      );

    const transitionHeightBlend = pedestrianTransition === null
      ? null
      : pedestrianTransitionHeightBlend(edgeSections, pedestrianTransition);
    appendTopStrip(
      carriageway,
      roadSections,
      -halfWidthM,
      halfWidthM,
      featureConfig.carriagewayLiftM,
      featureTerrain,
    );
    appendTopStrip(
      carriagewaySurfaces[streetFeatureRoadSurfaceClass(feature)],
      roadSections,
      -halfWidthM,
      halfWidthM,
      featureConfig.carriagewayLiftM,
      featureTerrain,
    );
    if (roadEdgeHalfWidthM > halfWidthM + 0.01) {
      for (const side of [-1, 1] as const) {
        appendTopStrip(
          shoulders,
          roadSections,
          Math.min(side * halfWidthM, side * roadEdgeHalfWidthM),
          Math.max(side * halfWidthM, side * roadEdgeHalfWidthM),
          featureConfig.carriagewayLiftM,
          featureTerrain,
        );
      }
    }
    const gradeProfile = streetGradeProfileForFeature(feature);
    if (gradeProfile !== null && gradeProfile.structure !== "subgrade") {
      appendElevatedDeckShell(
        elevatedDecks,
        edgeSections,
        roadEdgeHalfWidthM,
        featureConfig.carriagewayLiftM,
        gradeProfile.deckDepthM,
        featureTerrain,
      );
    }
    const segmentFilter = createIntersectionSegmentFilter(
      feature,
      corridors,
      featureConfig,
    );
    if (pedestrianTransition !== null && gatewayMaximumDepthM > 0.01) {
      appendPedestrianGatewayRoadBand(
        carriageway,
        edgeSections,
        pedestrianTransition,
        -halfWidthM,
        halfWidthM,
        halfWidthM,
        featureConfig.carriagewayLiftM,
        featureTerrain,
      );
      appendPedestrianGatewayRoadBand(
        carriagewaySurfaces[streetFeatureRoadSurfaceClass(feature)],
        edgeSections,
        pedestrianTransition,
        -halfWidthM,
        halfWidthM,
        halfWidthM,
        featureConfig.carriagewayLiftM,
        featureTerrain,
      );
      if (roadEdgeHalfWidthM > halfWidthM + 0.01) {
        for (const side of [-1, 1] as const) {
          appendPedestrianGatewayRoadBand(
            shoulders,
            edgeSections,
            pedestrianTransition,
            Math.min(side * halfWidthM, side * roadEdgeHalfWidthM),
            Math.max(side * halfWidthM, side * roadEdgeHalfWidthM),
            halfWidthM,
            featureConfig.carriagewayLiftM,
            featureTerrain,
          );
        }
      }
    }
    const sidewalkSides = streetFeatureSidewalkSides(feature);
    if (
      transitionHeightBlend !== null &&
      pedestrianTransition !== null &&
      feature.publicRealmWidthM !== undefined
    ) {
      appendPedestrianGatewayAprons(
        sidewalks,
        edgeSections,
        pedestrianTransition,
        sidewalkSides.length === 2
          ? roadEdgeHalfWidthM
          : feature.publicRealmWidthM / 2,
        halfWidthM,
        featureConfig.pedestrianLiftM,
        featureTerrain,
      );
    }
    for (const side of sidewalkSides) {
      const roadEdgeM = side * roadEdgeHalfWidthM;
      const curbOuterM =
        side * (roadEdgeHalfWidthM + featureConfig.curbWidthM);
      const sidewalkOuterM =
        side * (
          roadEdgeHalfWidthM +
          featureConfig.curbWidthM +
          featureConfig.sidewalkWidthM
        );
      if (transitionHeightBlend !== null) {
        const roadEdgeOffsetsM = transitionHeightBlend.map(() => roadEdgeM);
        const curbOuterOffsetsM = transitionHeightBlend.map(() => curbOuterM);
        const sidewalkOuterOffsetsM = transitionHeightBlend.map(() =>
          sidewalkOuterM
        );
        const sidewalkLiftsM = transitionHeightBlend.map((blend) =>
          featureConfig.pedestrianLiftM +
          (featureConfig.curbHeightM - featureConfig.pedestrianLiftM) * blend
        );
        appendVariableOffsetTopStrip(
          curbs,
          edgeSections,
          roadEdgeOffsetsM,
          curbOuterOffsetsM,
          sidewalkLiftsM,
          featureTerrain,
          segmentFilter,
        );
        appendVariableVerticalStrip(
          curbs,
          edgeSections,
          roadEdgeOffsetsM,
          transitionHeightBlend.map(() => featureConfig.carriagewayLiftM),
          sidewalkLiftsM,
          featureTerrain,
          segmentFilter,
        );
        appendVariableOffsetTopStrip(
          sidewalks,
          edgeSections,
          curbOuterOffsetsM,
          sidewalkOuterOffsetsM,
          sidewalkLiftsM,
          featureTerrain,
          segmentFilter,
        );
        appendVariableVerticalStrip(
          curbs,
          edgeSections,
          sidewalkOuterOffsetsM,
          transitionHeightBlend.map(() => featureConfig.carriagewayLiftM),
          sidewalkLiftsM,
          featureTerrain,
          segmentFilter,
        );
        continue;
      }
      appendTopStrip(
        curbs,
        edgeSections,
        Math.min(roadEdgeM, curbOuterM),
        Math.max(roadEdgeM, curbOuterM),
        featureConfig.curbHeightM,
        featureTerrain,
        segmentFilter,
      );
      appendVerticalStrip(
        curbs,
        edgeSections,
        roadEdgeM,
        featureConfig.carriagewayLiftM,
        featureConfig.curbHeightM,
        featureTerrain,
        segmentFilter,
      );
      appendTopStrip(
        sidewalks,
        edgeSections,
        Math.min(curbOuterM, sidewalkOuterM),
        Math.max(curbOuterM, sidewalkOuterM),
        featureConfig.curbHeightM,
        featureTerrain,
        segmentFilter,
      );
      appendVerticalStrip(
        curbs,
        edgeSections,
        sidewalkOuterM,
        featureConfig.carriagewayLiftM,
        featureConfig.curbHeightM,
        featureTerrain,
        segmentFilter,
      );
    }
    carriagewayFeatureIds.push(feature.id);
  }

  for (const junction of junctionTopology.junctions) {
    const gradeProfile = AUTHORED_STREET_GRADE_PROFILES.find(
      (profile) => profile.id === junction.gradeLayer,
    );
    const junctionTerrain = gradeProfile === undefined
      ? terrain
      : terrainForStreetGradeProfile(gradeProfile, terrain, tileOrigin);
    appendJunctionSurface(
      junctions,
      junction,
      junctionTerrain,
    );
    appendJunctionSurface(
      junctionSurfaces[dominantJunctionSurfaceClass(junction)],
      junction,
      junctionTerrain,
    );
    if (gradeProfile !== undefined && gradeProfile.structure !== "subgrade") {
      appendElevatedJunctionShell(
        elevatedDecks,
        junction,
        STREET_SURFACE_CONFIG.junctionSurfaceLiftM,
        gradeProfile.deckDepthM,
        junctionTerrain,
      );
    }
    for (const corner of createJunctionCorners(junction)) {
      if (!corner.hasSidewalk) {
        continue;
      }
      appendJunctionCornerVisuals(
        sidewalks,
        curbs,
        corner,
        familyConfig,
        junctionTerrain,
      );
    }
  }

  return {
    family,
    carriageway: freezeMesh(carriageway),
    carriagewaySurfaces: freezeRoadSurfaceMeshes(carriagewaySurfaces),
    shoulders: freezeMesh(shoulders),
    elevatedDecks: freezeMesh(elevatedDecks),
    junctions: freezeMesh(junctions),
    junctionSurfaces: freezeRoadSurfaceMeshes(junctionSurfaces),
    sidewalks: freezeMesh(sidewalks),
    curbs: freezeMesh(curbs),
    pedestrian: freezeMesh(pedestrian),
    pedestrianSurfaces: freezePedestrianSurfaceMeshes(pedestrianSurfaces),
    carriagewayFeatureIds,
    pedestrianFeatureIds,
    junctionCount: junctionTopology.junctions.length,
  };
}

export function createStreetWalkableColliderMesh(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
  family: StreetSurfaceFamily,
  options: Readonly<Pick<StreetSurfaceMeshOptions, "tileOrigin">> = {},
): Readonly<{
  vertices: Float32Array;
  indices: Uint32Array;
}> | null {
  const vertices: number[] = [];
  const indices: number[] = [];
  const familyConfig = STREET_SURFACE_CONFIG.families[family];
  const tileOrigin = options.tileOrigin ?? { x: 0, z: 0 };
  const corridors = createCarriagewayCorridors(features);
  const junctionTopology = createStreetJunctionTopology(
    features,
    family,
    terrain,
    tileOrigin,
  );
  for (const feature of features) {
    if (
      (feature.kind !== "road" && feature.kind !== "path") ||
      isPedestrianStreetFeature(feature)
    ) {
      continue;
    }
    const pedestrianTransition = pedestrianRoadTransitionForFeature(
      feature,
      features,
    );
    const maximumSegmentLengthM = pedestrianTransition === null
      ? STREET_SURFACE_CONFIG.maximumSegmentLengthM
      : STREET_SURFACE_CONFIG.pedestrianTransitionMaximumSegmentLengthM;
    const fullSections = createCrossSections(
      feature.points,
      0,
      0,
      maximumSegmentLengthM,
    );
    if (fullSections.length < 2) {
      continue;
    }
    const trim = junctionTopology.trimsByFeatureId.get(feature.id);
    const sections = createCrossSections(
      feature.points,
      trim?.startM ?? 0,
      trim?.endM ?? 0,
      maximumSegmentLengthM,
    );
    if (sections.length < 2) {
      continue;
    }
    const halfWidthM = streetFeatureRoadEdgeWidthM(feature) / 2;
    const featureConfig = streetSurfaceConfigForFeature(feature, family);
    const featureTerrain = terrainForStreetFeature(
      feature,
      terrain,
      tileOrigin,
    );
    const gradeProfile = streetGradeProfileForFeature(feature);
    if (gradeProfile !== null && gradeProfile.structure !== "subgrade") {
      appendElevatedWalkablePrism(
        vertices,
        indices,
        fullSections,
        -halfWidthM,
        halfWidthM,
        featureConfig.carriagewayLiftM,
        gradeProfile.deckDepthM,
        featureTerrain,
      );
    }
    const segmentFilter = createIntersectionSegmentFilter(
      feature,
      corridors,
      featureConfig,
    );
    const transitionHeightBlend = pedestrianTransition === null
      ? null
      : pedestrianTransitionHeightBlend(sections, pedestrianTransition);
    const sidewalkSides = streetFeatureSidewalkSides(feature);
    for (const side of sidewalkSides) {
      const roadEdgeM = side * halfWidthM;
      const sidewalkOuterM =
        side * (
          halfWidthM +
          featureConfig.curbWidthM +
          featureConfig.sidewalkWidthM
        );
      const minimumOffsetM = Math.min(roadEdgeM, sidewalkOuterM);
      const maximumOffsetM = Math.max(roadEdgeM, sidewalkOuterM);
      if (transitionHeightBlend !== null) {
        const roadEdgeOffsetsM = transitionHeightBlend.map(() => roadEdgeM);
        const sidewalkOuterOffsetsM = transitionHeightBlend.map(() =>
          sidewalkOuterM
        );
        const sidewalkLiftsM = transitionHeightBlend.map((blend) =>
          featureConfig.pedestrianLiftM +
          (featureConfig.curbHeightM - featureConfig.pedestrianLiftM) * blend
        );
        appendVariableWalkablePrism(
          vertices,
          indices,
          sections,
          roadEdgeOffsetsM,
          sidewalkOuterOffsetsM,
          sidewalkLiftsM,
          featureTerrain,
          segmentFilter,
        );
        continue;
      }
      for (const run of includedSectionRuns(
        sections,
        minimumOffsetM,
        maximumOffsetM,
        segmentFilter,
      )) {
        appendWalkablePrism(
          vertices,
          indices,
          run,
          minimumOffsetM,
          maximumOffsetM,
          featureConfig,
          featureTerrain,
        );
      }
    }
  }
  for (const junction of junctionTopology.junctions) {
    const gradeProfile = AUTHORED_STREET_GRADE_PROFILES.find(
      (profile) => profile.id === junction.gradeLayer,
    );
    const junctionTerrain = gradeProfile === undefined
      ? terrain
      : terrainForStreetGradeProfile(gradeProfile, terrain, tileOrigin);
    for (const corner of createJunctionCorners(junction)) {
      if (!corner.hasSidewalk) {
        continue;
      }
      appendWalkableRibbonPrism(
        vertices,
        indices,
        corner.inner,
        corner.sidewalkOuter,
        familyConfig,
        junctionTerrain,
      );
    }
  }
  if (vertices.length === 0) {
    return null;
  }
  return {
    vertices: new Float32Array(vertices),
    indices: new Uint32Array(indices),
  };
}

function streetGradeLayerAtPoint(
  feature: Readonly<RuntimeLinearFeature>,
  point: Readonly<LocalHorizontal>,
  terrain: Readonly<TerrainTileData>,
  tileOrigin: Readonly<LocalHorizontal>,
): string {
  const profile = streetGradeProfileForFeature(feature);
  if (profile === null) {
    return "ground";
  }
  const deckElevationM = authoredStreetElevationM(profile, {
    x: tileOrigin.x + point.x,
    z: tileOrigin.z + point.z,
  });
  if (deckElevationM === null) {
    return "ground";
  }
  const terrainElevationM = sampleHeight(terrain, point.x, point.z);
  const clearanceM = profile.structure === "subgrade"
    ? terrainElevationM - deckElevationM
    : deckElevationM - terrainElevationM;
  return clearanceM >
      profile.elevatedClearanceThresholdM
    ? profile.id
    : "ground";
}

function createStreetJunctionTopology(
  features: readonly Readonly<RuntimeLinearFeature>[],
  family: StreetSurfaceFamily,
  terrain: Readonly<TerrainTileData>,
  tileOrigin: Readonly<LocalHorizontal>,
): StreetJunctionTopology {
  const roadFeatures = features.filter((feature) =>
    (feature.kind === "road" || feature.kind === "path") &&
    !isPedestrianStreetFeature(feature) &&
    feature.points.length >= 2
  );
  const endpointClusters: StreetJunctionEndpoint[][] = [];
  for (const feature of roadFeatures) {
    const first = feature.points[0];
    const last = feature.points[feature.points.length - 1];
    if (first !== undefined) {
      addEndpointToCluster(endpointClusters, {
        feature,
        endpoint: "start",
        point: first,
        gradeLayer: streetGradeLayerAtPoint(
          feature,
          first,
          terrain,
          tileOrigin,
        ),
      });
    }
    if (
      last !== undefined &&
      (
        first === undefined ||
        Math.hypot(last.x - first.x, last.z - first.z) >
          STREET_SURFACE_CONFIG.junctionEndpointToleranceM
      )
    ) {
      addEndpointToCluster(endpointClusters, {
        feature,
        endpoint: "end",
        point: last,
        gradeLayer: streetGradeLayerAtPoint(
          feature,
          last,
          terrain,
          tileOrigin,
        ),
      });
    }
  }

  const candidates = endpointClusters
    .map((endpoints) => ({
      endpoints,
      legs: endpoints
        .map((endpoint) => streetJunctionCandidateLeg(endpoint, family))
        .filter((leg): leg is StreetJunctionCandidateLeg => leg !== null)
        .sort((left, right) => left.angleRadians - right.angleRadians),
    }))
    .filter(({ legs }) =>
      isStreetJunction(legs.map((leg) => ({
        x: leg.directionX,
        z: leg.directionZ,
      })))
    )
    .map(({ endpoints, legs }) => {
      const point = averagePoints(endpoints.map((endpoint) => endpoint.point));
      const maximumOuterHalfWidthM = Math.max(...legs.flatMap((leg) => [
        leg.leftOuterHalfWidthM,
        leg.rightOuterHalfWidthM,
      ]));
      const baseCutbackM = maximumOuterHalfWidthM +
        STREET_SURFACE_CONFIG.junctionCornerClearanceM;
      const cutbacksByEndpoint = new Map<StreetJunctionEndpoint, number>(
        endpoints.map((endpoint) => [endpoint, baseCutbackM]),
      );

      for (let index = 0; index < legs.length; index += 1) {
        const from = legs[index];
        const to = legs[(index + 1) % legs.length];
        if (from === undefined || to === undefined) {
          continue;
        }
        const counterClockwiseGapRadians = (
          to.angleRadians - from.angleRadians + Math.PI * 2
        ) % (Math.PI * 2);
        if (counterClockwiseGapRadians >= Math.PI - 1e-5) {
          continue;
        }
        const intersection = intersectRays(
          {
            x: point.x + from.normalX * from.leftOuterHalfWidthM,
            z: point.z + from.normalZ * from.leftOuterHalfWidthM,
          },
          { x: from.directionX, z: from.directionZ },
          {
            x: point.x - to.normalX * to.rightOuterHalfWidthM,
            z: point.z - to.normalZ * to.rightOuterHalfWidthM,
          },
          { x: to.directionX, z: to.directionZ },
        );
        if (
          intersection === null ||
          intersection.fromDistanceM < 0 ||
          intersection.toDistanceM < 0
        ) {
          continue;
        }
        const maximumCutbackM =
          STREET_SURFACE_CONFIG.junctionAcuteCornerMaximumCutbackM;
        cutbacksByEndpoint.set(
          from.endpoint,
          Math.max(
            cutbacksByEndpoint.get(from.endpoint) ?? baseCutbackM,
            Math.min(
              maximumCutbackM,
              intersection.fromDistanceM +
                STREET_SURFACE_CONFIG.junctionCornerClearanceM,
            ),
          ),
        );
        cutbacksByEndpoint.set(
          to.endpoint,
          Math.max(
            cutbacksByEndpoint.get(to.endpoint) ?? baseCutbackM,
            Math.min(
              maximumCutbackM,
              intersection.toDistanceM +
                STREET_SURFACE_CONFIG.junctionCornerClearanceM,
            ),
          ),
        );
      }
      return {
        endpoints,
        point,
        cutbacksByEndpoint,
      };
    });

  const mutableTrims = new Map<string, StreetFeatureTrim>();
  for (const candidate of candidates) {
    for (const endpoint of candidate.endpoints) {
      const cutbackM = candidate.cutbacksByEndpoint.get(endpoint);
      if (cutbackM === undefined) {
        continue;
      }
      const trim = mutableTrims.get(endpoint.feature.id) ?? {
        startM: 0,
        endM: 0,
      };
      if (endpoint.endpoint === "start") {
        trim.startM = Math.max(trim.startM, cutbackM);
      } else {
        trim.endM = Math.max(trim.endM, cutbackM);
      }
      mutableTrims.set(endpoint.feature.id, trim);
    }
  }
  for (const feature of roadFeatures) {
    const trim = mutableTrims.get(feature.id);
    if (trim === undefined) {
      continue;
    }
    const lengthM = polylineLengthM(feature.points);
    const availableM = Math.max(0, lengthM - 0.1);
    const requestedM = trim.startM + trim.endM;
    if (requestedM > availableM && requestedM > 0) {
      const scale = availableM / requestedM;
      trim.startM *= scale;
      trim.endM *= scale;
    }
  }

  const junctions: StreetJunction[] = [];
  for (const candidate of candidates) {
    const legs = candidate.endpoints
      .map((endpoint) => {
        const trim = mutableTrims.get(endpoint.feature.id);
        if (trim === undefined) {
          return null;
        }
        const cutbackM = endpoint.endpoint === "start"
          ? trim.startM
          : trim.endM;
        const sample = sampleEndpointLeg(
          endpoint.feature.points,
          endpoint.endpoint,
          cutbackM,
        );
        if (sample === null) {
          return null;
        }
        const normalX = -sample.directionZ;
        const normalZ = sample.directionX;
        const config = streetSurfaceConfigForFeature(
          endpoint.feature,
          family,
        );
        const sourceSides = streetFeatureSidewalkSides(endpoint.feature);
        const sourceLeft = sourceSides.includes(1);
        const sourceRight = sourceSides.includes(-1);
        const sidewalkLeft = endpoint.endpoint === "start"
          ? sourceLeft
          : sourceRight;
        const sidewalkRight = endpoint.endpoint === "start"
          ? sourceRight
          : sourceLeft;
        return {
          featureId: endpoint.feature.id,
          surfaceClass: streetFeatureRoadSurfaceClass(endpoint.feature),
          centre: sample.point,
          directionX: sample.directionX,
          directionZ: sample.directionZ,
          normalX,
          normalZ,
          halfWidthM: streetFeatureRoadEdgeWidthM(endpoint.feature) / 2,
          curbWidthM: config.curbWidthM,
          sidewalkWidthM: config.sidewalkWidthM,
          sidewalkLeft,
          sidewalkRight,
          angleRadians: Math.atan2(sample.directionZ, sample.directionX),
        } satisfies StreetJunctionLeg;
      })
      .filter((leg): leg is StreetJunctionLeg => leg !== null)
      .sort((left, right) => left.angleRadians - right.angleRadians);
    if (legs.length >= 2) {
      junctions.push({
        point: candidate.point,
        cutbackM: Math.max(
          ...candidate.endpoints.map((endpoint) => {
            const trim = mutableTrims.get(endpoint.feature.id);
            return endpoint.endpoint === "start"
              ? trim?.startM ?? 0
              : trim?.endM ?? 0;
          }),
        ),
        gradeLayer: candidate.endpoints[0]?.gradeLayer ?? "ground",
        legs,
      });
    }
  }
  return {
    junctions,
    trimsByFeatureId: mutableTrims,
  };
}

function streetJunctionCandidateLeg(
  endpoint: Readonly<StreetJunctionEndpoint>,
  family: StreetSurfaceFamily,
): StreetJunctionCandidateLeg | null {
  const direction = endpointDirection(endpoint);
  if (direction === null) {
    return null;
  }
  const normalX = -direction.z;
  const normalZ = direction.x;
  const config = streetSurfaceConfigForFeature(endpoint.feature, family);
  const sourceSides = streetFeatureSidewalkSides(endpoint.feature);
  const sourceLeft = sourceSides.includes(1);
  const sourceRight = sourceSides.includes(-1);
  const sidewalkLeft = endpoint.endpoint === "start"
    ? sourceLeft
    : sourceRight;
  const sidewalkRight = endpoint.endpoint === "start"
    ? sourceRight
    : sourceLeft;
  const roadEdgeHalfWidthM = streetFeatureRoadEdgeWidthM(endpoint.feature) / 2;
  const sidewalkOffsetM = config.curbWidthM + config.sidewalkWidthM;
  return {
    endpoint,
    directionX: direction.x,
    directionZ: direction.z,
    normalX,
    normalZ,
    leftOuterHalfWidthM:
      roadEdgeHalfWidthM + (sidewalkLeft ? sidewalkOffsetM : 0),
    rightOuterHalfWidthM:
      roadEdgeHalfWidthM + (sidewalkRight ? sidewalkOffsetM : 0),
    angleRadians: Math.atan2(direction.z, direction.x),
  };
}

function dominantJunctionSurfaceClass(
  junction: Readonly<StreetJunction>,
): RoadSurfaceClass {
  const weights = new Map<RoadSurfaceClass, number>();
  for (const leg of junction.legs) {
    weights.set(
      leg.surfaceClass,
      (weights.get(leg.surfaceClass) ?? 0) + leg.halfWidthM,
    );
  }
  return ROAD_SURFACE_CLASSES.reduce((dominant, candidate) =>
    (weights.get(candidate) ?? 0) > (weights.get(dominant) ?? 0)
      ? candidate
      : dominant
  );
}

function addEndpointToCluster(
  clusters: StreetJunctionEndpoint[][],
  endpoint: StreetJunctionEndpoint,
): void {
  const toleranceM = STREET_SURFACE_CONFIG.junctionEndpointToleranceM;
  const cluster = clusters.find((candidate) =>
    candidate.some((member) =>
      endpoint.gradeLayer === member.gradeLayer &&
      Math.hypot(
        endpoint.point.x - member.point.x,
        endpoint.point.z - member.point.z,
      ) <= toleranceM
    )
  );
  if (cluster === undefined) {
    clusters.push([endpoint]);
  } else {
    cluster.push(endpoint);
  }
}

function endpointDirection(
  endpoint: Readonly<StreetJunctionEndpoint>,
): LocalHorizontal | null {
  const points = endpoint.feature.points;
  const originIndex = endpoint.endpoint === "start" ? 0 : points.length - 1;
  const step = endpoint.endpoint === "start" ? 1 : -1;
  const origin = points[originIndex];
  if (origin === undefined) {
    return null;
  }
  for (
    let index = originIndex + step;
    index >= 0 && index < points.length;
    index += step
  ) {
    const candidate = points[index];
    if (candidate === undefined) {
      continue;
    }
    const deltaX = candidate.x - origin.x;
    const deltaZ = candidate.z - origin.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM > 0.01) {
      return { x: deltaX / lengthM, z: deltaZ / lengthM };
    }
  }
  return null;
}

function isStreetJunction(
  directions: readonly Readonly<LocalHorizontal>[],
): boolean {
  const unique: LocalHorizontal[] = [];
  for (const direction of directions) {
    const duplicated = unique.some((candidate) =>
      candidate.x * direction.x + candidate.z * direction.z >
        Math.cos(Math.PI / 36)
    );
    if (!duplicated) {
      unique.push({ x: direction.x, z: direction.z });
    }
  }
  if (unique.length >= 3) {
    return true;
  }
  if (unique.length !== 2) {
    return false;
  }
  const separationRadians = Math.acos(Math.max(
    -1,
    Math.min(1, unique[0]!.x * unique[1]!.x + unique[0]!.z * unique[1]!.z),
  ));
  return Math.PI - separationRadians > Math.PI / 18;
}

function averagePoints(
  points: readonly Readonly<LocalHorizontal>[],
): LocalHorizontal {
  const sum = points.reduce(
    (result, point) => ({
      x: result.x + point.x,
      z: result.z + point.z,
    }),
    { x: 0, z: 0 },
  );
  return {
    x: sum.x / points.length,
    z: sum.z / points.length,
  };
}

function polylineLengthM(
  points: readonly Readonly<LocalHorizontal>[],
): number {
  let lengthM = 0;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from !== undefined && to !== undefined) {
      lengthM += Math.hypot(to.x - from.x, to.z - from.z);
    }
  }
  return lengthM;
}

function extendStreetPolylineAtTileBoundary(
  points: readonly Readonly<LocalHorizontal>[],
  terrain: Readonly<TerrainTileData>,
  extensionM: number,
): readonly LocalHorizontal[] {
  // A diagonal road clipped at a tile edge otherwise leaves a small corner
  // wedge covered only by the neighbouring tile's height-extrapolated ribbon.
  // Local terrain can then occlude that ribbon and appear through the asphalt.
  if (points.length < 2 || extensionM <= 0) {
    return points;
  }
  const result = points.map((point) => ({ x: point.x, z: point.z }));
  const maximumM = terrain.spacingM * (terrain.samplesPerSide - 1);
  const toleranceM = STREET_SURFACE_CONFIG.junctionEndpointToleranceM;
  const touchesBoundary = (point: Readonly<LocalHorizontal>) =>
    Math.abs(point.x) <= toleranceM ||
    Math.abs(point.z) <= toleranceM ||
    Math.abs(point.x - maximumM) <= toleranceM ||
    Math.abs(point.z - maximumM) <= toleranceM;
  const extend = (
    endpointIndex: number,
    neighbourIndex: number,
  ) => {
    const endpoint = result[endpointIndex];
    const neighbour = result[neighbourIndex];
    if (
      endpoint === undefined ||
      neighbour === undefined ||
      !touchesBoundary(endpoint)
    ) {
      return;
    }
    const deltaX = endpoint.x - neighbour.x;
    const deltaZ = endpoint.z - neighbour.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    // Only an oblique crossing needs an axial extension to cover the corner
    // wedge produced by clipping. A road perpendicular to the tile edge
    // already spans it through its cross-section; extending that centreline
    // would create geometry outside the owning heightfield for no visual gain.
    if (
      lengthM <= 0.01 ||
      Math.abs(deltaX) <= 0.01 ||
      Math.abs(deltaZ) <= 0.01
    ) {
      return;
    }
    result[endpointIndex] = {
      x: endpoint.x + deltaX / lengthM * extensionM,
      z: endpoint.z + deltaZ / lengthM * extensionM,
    };
  };
  extend(0, 1);
  extend(result.length - 1, result.length - 2);
  return result;
}

function sampleEndpointLeg(
  points: readonly Readonly<LocalHorizontal>[],
  endpoint: "start" | "end",
  cutbackM: number,
): Readonly<{
  point: LocalHorizontal;
  directionX: number;
  directionZ: number;
}> | null {
  const totalLengthM = polylineLengthM(points);
  const distanceM = endpoint === "start"
    ? cutbackM
    : totalLengthM - cutbackM;
  const sample = samplePolylineAtDistance(points, distanceM);
  if (sample === null) {
    return null;
  }
  const directionScale = endpoint === "start" ? 1 : -1;
  return {
    point: sample.point,
    directionX: sample.tangentX * directionScale,
    directionZ: sample.tangentZ * directionScale,
  };
}

function samplePolylineAtDistance(
  points: readonly Readonly<LocalHorizontal>[],
  requestedDistanceM: number,
): Readonly<{
  point: LocalHorizontal;
  tangentX: number;
  tangentZ: number;
}> | null {
  const totalLengthM = polylineLengthM(points);
  const distanceM = Math.max(0, Math.min(totalLengthM, requestedDistanceM));
  let traversedM = 0;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM <= 0.01) {
      continue;
    }
    if (traversedM + lengthM + 1e-6 >= distanceM) {
      const t = Math.max(0, Math.min(1, (distanceM - traversedM) / lengthM));
      return {
        point: {
          x: from.x + deltaX * t,
          z: from.z + deltaZ * t,
        },
        tangentX: deltaX / lengthM,
        tangentZ: deltaZ / lengthM,
      };
    }
    traversedM += lengthM;
  }
  return null;
}

function createJunctionCorners(
  junction: Readonly<StreetJunction>,
): readonly JunctionCorner[] {
  const corners: JunctionCorner[] = [];
  for (let index = 0; index < junction.legs.length; index += 1) {
    const from = junction.legs[index];
    const to = junction.legs[(index + 1) % junction.legs.length];
    if (from === undefined || to === undefined) {
      continue;
    }
    corners.push({
      inner: createCornerCurve(
        junction,
        from,
        to,
        from.halfWidthM,
        to.halfWidthM,
      ),
      curbOuter: createCornerCurve(
        junction,
        from,
        to,
        from.halfWidthM + from.curbWidthM,
        to.halfWidthM + to.curbWidthM,
      ),
      sidewalkOuter: createCornerCurve(
        junction,
        from,
        to,
        from.halfWidthM + from.curbWidthM + from.sidewalkWidthM,
        to.halfWidthM + to.curbWidthM + to.sidewalkWidthM,
      ),
      hasSidewalk: from.sidewalkLeft && to.sidewalkRight,
    });
  }
  return corners;
}

function createCornerCurve(
  junction: Readonly<StreetJunction>,
  from: Readonly<StreetJunctionLeg>,
  to: Readonly<StreetJunctionLeg>,
  fromOffsetM: number,
  toOffsetM: number,
): readonly LocalHorizontal[] {
  const start = {
    x: from.centre.x + from.normalX * fromOffsetM,
    z: from.centre.z + from.normalZ * fromOffsetM,
  };
  const end = {
    x: to.centre.x - to.normalX * toOffsetM,
    z: to.centre.z - to.normalZ * toOffsetM,
  };
  const intersection = intersectRays(
    start,
    { x: -from.directionX, z: -from.directionZ },
    end,
    { x: -to.directionX, z: -to.directionZ },
  );
  const counterClockwiseGapRadians =
    (
      to.angleRadians -
      from.angleRadians +
      Math.PI * 2
    ) % (Math.PI * 2);
  const nearlyStraight =
    Math.abs(counterClockwiseGapRadians - Math.PI) <= Math.PI / 9;
  const maximumControlDistanceM = junction.cutbackM * 2.5;
  const control = !nearlyStraight &&
      intersection !== null &&
      intersection.fromDistanceM >= 0 &&
      intersection.toDistanceM >= 0 &&
      Math.hypot(
          intersection.point.x - junction.point.x,
          intersection.point.z - junction.point.z,
        ) <= maximumControlDistanceM
    ? intersection.point
    : {
      x: (start.x + end.x) / 2,
      z: (start.z + end.z) / 2,
    };
  const points: LocalHorizontal[] = [];
  for (
    let section = 0;
    section <= STREET_SURFACE_CONFIG.junctionCornerSegments;
    section += 1
  ) {
    const t = section / STREET_SURFACE_CONFIG.junctionCornerSegments;
    const inverseT = 1 - t;
    points.push({
      x:
        inverseT * inverseT * start.x +
        2 * inverseT * t * control.x +
        t * t * end.x,
      z:
        inverseT * inverseT * start.z +
        2 * inverseT * t * control.z +
        t * t * end.z,
    });
  }
  return points;
}

function intersectRays(
  from: Readonly<LocalHorizontal>,
  fromDirection: Readonly<LocalHorizontal>,
  to: Readonly<LocalHorizontal>,
  toDirection: Readonly<LocalHorizontal>,
): Readonly<{
  point: LocalHorizontal;
  fromDistanceM: number;
  toDistanceM: number;
}> | null {
  const determinant =
    fromDirection.x * toDirection.z -
    fromDirection.z * toDirection.x;
  if (Math.abs(determinant) <= 1e-5) {
    return null;
  }
  const deltaX = to.x - from.x;
  const deltaZ = to.z - from.z;
  const fromDistanceM =
    (deltaX * toDirection.z - deltaZ * toDirection.x) / determinant;
  const toDistanceM =
    (deltaX * fromDirection.z - deltaZ * fromDirection.x) / determinant;
  return {
    point: {
      x: from.x + fromDirection.x * fromDistanceM,
      z: from.z + fromDirection.z * fromDistanceM,
    },
    fromDistanceM,
    toDistanceM,
  };
}

function appendJunctionSurface(
  mesh: MutableStreetMeshData,
  junction: Readonly<StreetJunction>,
  terrain: Readonly<TerrainTileData>,
): void {
  appendTopPolygon(
    mesh,
    createJunctionCorners(junction)
      .flatMap((corner) => corner.inner),
    STREET_SURFACE_CONFIG.junctionSurfaceLiftM,
    terrain,
  );
}

function appendJunctionCornerVisuals(
  sidewalkMesh: MutableStreetMeshData,
  curbMesh: MutableStreetMeshData,
  corner: Readonly<JunctionCorner>,
  config: Readonly<StreetSurfaceFamilyConfig>,
  terrain: Readonly<TerrainTileData>,
): void {
  appendTopRibbon(
    curbMesh,
    corner.inner,
    corner.curbOuter,
    config.curbHeightM,
    terrain,
  );
  appendTopRibbon(
    sidewalkMesh,
    corner.curbOuter,
    corner.sidewalkOuter,
    config.curbHeightM,
    terrain,
  );
  appendVerticalCurve(
    curbMesh,
    corner.inner,
    config.carriagewayLiftM,
    config.curbHeightM,
    terrain,
  );
  appendVerticalCurve(
    curbMesh,
    corner.sidewalkOuter,
    config.carriagewayLiftM,
    config.curbHeightM,
    terrain,
  );
}

function appendTopRibbon(
  mesh: MutableStreetMeshData,
  inner: readonly Readonly<LocalHorizontal>[],
  outer: readonly Readonly<LocalHorizontal>[],
  liftM: number,
  terrain: Readonly<TerrainTileData>,
): void {
  appendTopPolygon(
    mesh,
    createRibbonBoundary(inner, outer),
    liftM,
    terrain,
  );
}

function createRibbonBoundary(
  inner: readonly Readonly<LocalHorizontal>[],
  outer: readonly Readonly<LocalHorizontal>[],
): readonly LocalHorizontal[] {
  return deduplicatePolygonPoints([
    ...inner,
    ...[...outer].reverse(),
  ]);
}

function deduplicatePolygonPoints(
  points: readonly Readonly<LocalHorizontal>[],
): readonly LocalHorizontal[] {
  const result: LocalHorizontal[] = [];
  for (const point of points) {
    const previous = result.at(-1);
    if (
      previous === undefined ||
      Math.hypot(point.x - previous.x, point.z - previous.z) > 1e-6
    ) {
      result.push({ x: point.x, z: point.z });
    }
  }
  const first = result[0];
  const last = result.at(-1);
  if (
    first !== undefined &&
    last !== undefined &&
    result.length > 1 &&
    Math.hypot(first.x - last.x, first.z - last.z) <= 1e-6
  ) {
    result.pop();
  }
  return result;
}

function appendTopPolygon(
  mesh: MutableStreetMeshData,
  rawBoundary: readonly Readonly<LocalHorizontal>[],
  liftM: number,
  terrain: Readonly<TerrainTileData>,
): void {
  const boundary = deduplicatePolygonPoints(rawBoundary);
  const triangles = triangulateSimplePolygon(boundary);
  for (const [first, second, third] of triangles) {
    const firstPoint = boundary[first];
    const secondPoint = boundary[second];
    const thirdPoint = boundary[third];
    if (
      firstPoint === undefined ||
      secondPoint === undefined ||
      thirdPoint === undefined
    ) {
      continue;
    }
    appendTerrainDrapedTriangle(
      mesh,
      [firstPoint, secondPoint, thirdPoint],
      liftM,
      terrain,
    );
  }
}

function appendTerrainDrapedTriangle(
  mesh: MutableStreetMeshData,
  sourceTriangle: readonly [
    Readonly<LocalHorizontal>,
    Readonly<LocalHorizontal>,
    Readonly<LocalHorizontal>,
  ],
  liftM: number,
  terrain: Readonly<TerrainTileData>,
): void {
  // Street ribbons intentionally overhang a tile edge by their half-width so
  // the independently built neighbour can overlap them without a diagonal
  // crack. Extend one synthetic heightfield cell beyond each edge; height
  // sampling clamps that apron to the shared boundary samples.
  const minimumCell = -1;
  const maximumCell = terrain.samplesPerSide - 1;
  const minimumX = Math.min(...sourceTriangle.map((point) => point.x));
  const maximumX = Math.max(...sourceTriangle.map((point) => point.x));
  const minimumZ = Math.min(...sourceTriangle.map((point) => point.z));
  const maximumZ = Math.max(...sourceTriangle.map((point) => point.z));
  const startColumn = Math.max(
    minimumCell,
    Math.min(maximumCell, Math.floor(minimumX / terrain.spacingM)),
  );
  const endColumn = Math.max(
    minimumCell,
    Math.min(maximumCell, Math.floor(maximumX / terrain.spacingM)),
  );
  const startRow = Math.max(
    minimumCell,
    Math.min(maximumCell, Math.floor(minimumZ / terrain.spacingM)),
  );
  const endRow = Math.max(
    minimumCell,
    Math.min(maximumCell, Math.floor(maximumZ / terrain.spacingM)),
  );

  for (let row = startRow; row <= endRow; row += 1) {
    for (let column = startColumn; column <= endColumn; column += 1) {
      const west = column * terrain.spacingM;
      const east = west + terrain.spacingM;
      const north = row * terrain.spacingM;
      const south = north + terrain.spacingM;
      const northWest = { x: west, z: north };
      const northEast = { x: east, z: north };
      const southWest = { x: west, z: south };
      const southEast = { x: east, z: south };
      for (const terrainTriangle of [
        [northWest, southWest, northEast],
        [northEast, southWest, southEast],
      ] as const) {
        const clipped = clipStreetPolygon(
          sourceTriangle,
          terrainTriangle,
        );
        const anchor = clipped[0];
        if (anchor === undefined) {
          continue;
        }
        for (let index = 2; index < clipped.length; index += 1) {
          const second = clipped[index - 1];
          const third = clipped[index];
          if (
            second === undefined ||
            third === undefined ||
            Math.abs(signedTriangleArea(anchor, second, third)) <= 1e-8
          ) {
            continue;
          }
          appendDrapedTopTriangle(
            mesh,
            [anchor, second, third],
            liftM,
            terrain,
          );
        }
      }
    }
  }
}

function clipStreetPolygon(
  subject: readonly Readonly<LocalHorizontal>[],
  clip: readonly Readonly<LocalHorizontal>[],
): readonly LocalHorizontal[] {
  const orientation = signedPolygonArea(clip) >= 0 ? 1 : -1;
  let output = subject.map((point) => ({ x: point.x, z: point.z }));
  for (let edgeIndex = 0; edgeIndex < clip.length; edgeIndex += 1) {
    const edgeStart = clip[edgeIndex];
    const edgeEnd = clip[(edgeIndex + 1) % clip.length];
    if (edgeStart === undefined || edgeEnd === undefined) {
      continue;
    }
    const input = output;
    output = [];
    let previous = input.at(-1);
    if (previous === undefined) {
      break;
    }
    let previousDistance = signedStreetEdgeDistance(
      edgeStart,
      edgeEnd,
      previous,
      orientation,
    );
    for (const current of input) {
      const currentDistance = signedStreetEdgeDistance(
        edgeStart,
        edgeEnd,
        current,
        orientation,
      );
      const previousInside = previousDistance >= -1e-7;
      const currentInside = currentDistance >= -1e-7;
      if (previousInside !== currentInside) {
        const denominator = previousDistance - currentDistance;
        const t = Math.abs(denominator) <= 1e-12
          ? 0
          : previousDistance / denominator;
        output.push({
          x: previous.x + (current.x - previous.x) * t,
          z: previous.z + (current.z - previous.z) * t,
        });
      }
      if (currentInside) {
        output.push(current);
      }
      previous = current;
      previousDistance = currentDistance;
    }
  }
  return output;
}

function signedStreetEdgeDistance(
  edgeStart: Readonly<LocalHorizontal>,
  edgeEnd: Readonly<LocalHorizontal>,
  point: Readonly<LocalHorizontal>,
  orientation: number,
): number {
  return orientation * (
    (edgeEnd.x - edgeStart.x) * (point.z - edgeStart.z) -
    (edgeEnd.z - edgeStart.z) * (point.x - edgeStart.x)
  );
}

function appendDrapedTopTriangle(
  mesh: MutableStreetMeshData,
  points: readonly [
    Readonly<LocalHorizontal>,
    Readonly<LocalHorizontal>,
    Readonly<LocalHorizontal>,
  ],
  liftM: number,
  terrain: Readonly<TerrainTileData>,
): void {
  const vertexOffset = mesh.positions.length / 3;
  for (const point of points) {
    mesh.positions.push(
      point.x,
      sampleHeight(terrain, point.x, point.z) + liftM,
      point.z,
    );
    mesh.uvs.push(point.x, point.z);
  }
  if (signedTriangleArea(points[0], points[1], points[2]) <= 0) {
    mesh.indices.push(vertexOffset, vertexOffset + 1, vertexOffset + 2);
  } else {
    mesh.indices.push(vertexOffset, vertexOffset + 2, vertexOffset + 1);
  }
}

function triangulateSimplePolygon(
  points: readonly Readonly<LocalHorizontal>[],
): readonly (readonly [number, number, number])[] {
  if (points.length < 3) {
    return [];
  }
  const orientation = signedPolygonArea(points) >= 0 ? 1 : -1;
  const remaining = points.map((_, index) => index);
  const triangles: [number, number, number][] = [];
  const maximumIterations = points.length * points.length;
  let iterations = 0;
  while (remaining.length > 3 && iterations < maximumIterations) {
    let clippedEar = false;
    for (let index = 0; index < remaining.length; index += 1) {
      const previousIndex =
        remaining[(index - 1 + remaining.length) % remaining.length];
      const currentIndex = remaining[index];
      const nextIndex = remaining[(index + 1) % remaining.length];
      if (
        previousIndex === undefined ||
        currentIndex === undefined ||
        nextIndex === undefined
      ) {
        continue;
      }
      const previous = points[previousIndex];
      const current = points[currentIndex];
      const next = points[nextIndex];
      if (
        previous === undefined ||
        current === undefined ||
        next === undefined ||
        orientation * signedTriangleArea(previous, current, next) <= 1e-8
      ) {
        continue;
      }
      const containsVertex = remaining.some((candidateIndex) => {
        if (
          candidateIndex === previousIndex ||
          candidateIndex === currentIndex ||
          candidateIndex === nextIndex
        ) {
          return false;
        }
        const candidate = points[candidateIndex];
        return candidate !== undefined &&
          pointInsideTriangle(
            candidate,
            previous,
            current,
            next,
            orientation,
          );
      });
      if (containsVertex) {
        continue;
      }
      triangles.push([previousIndex, currentIndex, nextIndex]);
      remaining.splice(index, 1);
      clippedEar = true;
      break;
    }
    if (!clippedEar) {
      return [];
    }
    iterations += 1;
  }
  if (remaining.length === 3) {
    const first = remaining[0];
    const second = remaining[1];
    const third = remaining[2];
    if (first !== undefined && second !== undefined && third !== undefined) {
      triangles.push([first, second, third]);
    }
  }
  return triangles;
}

function pointInsideTriangle(
  point: Readonly<LocalHorizontal>,
  first: Readonly<LocalHorizontal>,
  second: Readonly<LocalHorizontal>,
  third: Readonly<LocalHorizontal>,
  orientation: number,
): boolean {
  const epsilon = -1e-8;
  return orientation * signedTriangleArea(first, second, point) >= epsilon &&
    orientation * signedTriangleArea(second, third, point) >= epsilon &&
    orientation * signedTriangleArea(third, first, point) >= epsilon;
}

function signedTriangleArea(
  first: Readonly<LocalHorizontal>,
  second: Readonly<LocalHorizontal>,
  third: Readonly<LocalHorizontal>,
): number {
  return (
    (second.x - first.x) * (third.z - first.z) -
    (second.z - first.z) * (third.x - first.x)
  ) / 2;
}

function signedPolygonArea(
  points: readonly Readonly<LocalHorizontal>[],
): number {
  let twiceArea = 0;
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    const next = points[(index + 1) % points.length];
    if (point !== undefined && next !== undefined) {
      twiceArea += point.x * next.z - next.x * point.z;
    }
  }
  return twiceArea / 2;
}

function appendTopQuad(
  mesh: MutableStreetMeshData,
  points: readonly [
    Readonly<LocalHorizontal>,
    Readonly<LocalHorizontal>,
    Readonly<LocalHorizontal>,
    Readonly<LocalHorizontal>,
  ],
  liftM: number,
  terrain: Readonly<TerrainTileData>,
): void {
  appendTerrainDrapedTriangle(
    mesh,
    [points[0], points[1], points[2]],
    liftM,
    terrain,
  );
  appendTerrainDrapedTriangle(
    mesh,
    [points[0], points[2], points[3]],
    liftM,
    terrain,
  );
}

function appendVerticalCurve(
  mesh: MutableStreetMeshData,
  points: readonly Readonly<LocalHorizontal>[],
  lowerLiftM: number,
  upperLiftM: number,
  terrain: Readonly<TerrainTileData>,
): void {
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const fromHeight = sampleHeight(terrain, from.x, from.z);
    const toHeight = sampleHeight(terrain, to.x, to.z);
    const vertexOffset = mesh.positions.length / 3;
    mesh.positions.push(
      from.x,
      fromHeight + lowerLiftM,
      from.z,
      from.x,
      fromHeight + upperLiftM,
      from.z,
      to.x,
      toHeight + upperLiftM,
      to.z,
      to.x,
      toHeight + lowerLiftM,
      to.z,
    );
    mesh.uvs.push(
      0,
      lowerLiftM,
      0,
      upperLiftM,
      1,
      upperLiftM,
      1,
      lowerLiftM,
    );
    mesh.indices.push(
      vertexOffset,
      vertexOffset + 1,
      vertexOffset + 2,
      vertexOffset,
      vertexOffset + 2,
      vertexOffset + 3,
    );
  }
}

function appendWalkableRibbonPrism(
  vertices: number[],
  indices: number[],
  inner: readonly Readonly<LocalHorizontal>[],
  outer: readonly Readonly<LocalHorizontal>[],
  config: Readonly<StreetSurfaceFamilyConfig>,
  terrain: Readonly<TerrainTileData>,
): void {
  const boundary = createRibbonBoundary(inner, outer);
  const triangles = triangulateSimplePolygon(boundary);
  if (triangles.length === 0) {
    return;
  }
  const vertexOffset = vertices.length / 3;
  for (const point of boundary) {
    const height = sampleHeight(terrain, point.x, point.z);
    vertices.push(
      point.x,
      height - 0.02,
      point.z,
      point.x,
      height + config.curbHeightM,
      point.z,
    );
  }
  for (const [firstIndex, secondIndex, thirdIndex] of triangles) {
    const first = boundary[firstIndex];
    const second = boundary[secondIndex];
    const third = boundary[thirdIndex];
    if (first === undefined || second === undefined || third === undefined) {
      continue;
    }
    const firstBottom = vertexOffset + firstIndex * 2;
    const secondBottom = vertexOffset + secondIndex * 2;
    const thirdBottom = vertexOffset + thirdIndex * 2;
    const firstTop = firstBottom + 1;
    const secondTop = secondBottom + 1;
    const thirdTop = thirdBottom + 1;
    if (signedTriangleArea(first, second, third) <= 0) {
      indices.push(
        firstTop,
        secondTop,
        thirdTop,
        firstBottom,
        thirdBottom,
        secondBottom,
      );
    } else {
      indices.push(
        firstTop,
        thirdTop,
        secondTop,
        firstBottom,
        secondBottom,
        thirdBottom,
      );
    }
  }
  for (let index = 0; index < boundary.length; index += 1) {
    const nextIndex = (index + 1) % boundary.length;
    const fromBottom = vertexOffset + index * 2;
    const fromTop = fromBottom + 1;
    const toBottom = vertexOffset + nextIndex * 2;
    const toTop = toBottom + 1;
    indices.push(
      fromBottom,
      fromTop,
      toTop,
      fromBottom,
      toTop,
      toBottom,
    );
  }
}

function createCarriagewayCorridors(
  features: readonly Readonly<RuntimeLinearFeature>[],
): readonly CarriagewayCorridorSegment[] {
  const corridors: CarriagewayCorridorSegment[] = [];
  for (const feature of features) {
    if (
      (feature.kind !== "road" && feature.kind !== "path") ||
      isPedestrianStreetFeature(feature)
    ) {
      continue;
    }
    const halfWidthM = streetFeatureRoadEdgeWidthM(feature) / 2;
    const gradeProfileId = streetGradeProfileForFeature(feature)?.id ?? null;
    for (let index = 1; index < feature.points.length; index += 1) {
      const from = feature.points[index - 1];
      const to = feature.points[index];
      if (
        from === undefined ||
        to === undefined ||
        Math.hypot(to.x - from.x, to.z - from.z) <= 0.01
      ) {
        continue;
      }
      corridors.push({
        featureId: feature.id,
        featureName: feature.name?.trim() || null,
        from,
        to,
        halfWidthM,
        gradeProfileId,
      });
    }
  }
  return corridors;
}

function createIntersectionSegmentFilter(
  feature: Readonly<RuntimeLinearFeature>,
  corridors: readonly CarriagewayCorridorSegment[],
  config: Readonly<StreetSurfaceFamilyConfig>,
): StreetSegmentFilter {
  const featureName = feature.name?.trim() || null;
  const gradeProfileId = streetGradeProfileForFeature(feature)?.id ?? null;
  return (from, to, minimumOffsetM, maximumOffsetM) => {
    const middleOffsetM = (minimumOffsetM + maximumOffsetM) / 2;
    const normalX = (from.normalX + to.normalX) / 2;
    const normalZ = (from.normalZ + to.normalZ) / 2;
    const normalLengthM = Math.hypot(normalX, normalZ) || 1;
    const midpoint = {
      x:
        (from.point.x + to.point.x) / 2 +
        normalX / normalLengthM * middleOffsetM,
      z:
        (from.point.z + to.point.z) / 2 +
        normalZ / normalLengthM * middleOffsetM,
    };
    return !corridors.some((corridor) => {
      if (corridor.featureId === feature.id) {
        return false;
      }
      if (corridor.gradeProfileId !== gradeProfileId) {
        return false;
      }
      if (
        featureName !== null &&
        corridor.featureName !== null &&
        featureName === corridor.featureName
      ) {
        return false;
      }
      return pointToSegmentDistanceM(
        midpoint,
        corridor.from,
        corridor.to,
      ) <= corridor.halfWidthM + config.curbWidthM;
    });
  };
}

function includedSectionRuns(
  sections: readonly StreetCrossSection[],
  minimumOffsetM: number,
  maximumOffsetM: number,
  filter: StreetSegmentFilter,
): readonly (readonly StreetCrossSection[])[] {
  const runs: StreetCrossSection[][] = [];
  let current: StreetCrossSection[] = [];
  for (let index = 1; index < sections.length; index += 1) {
    const from = sections[index - 1];
    const to = sections[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    if (!filter(from, to, minimumOffsetM, maximumOffsetM)) {
      if (current.length >= 2) {
        runs.push(current);
      }
      current = [];
      continue;
    }
    if (current.length === 0) {
      current.push(from);
    }
    current.push(to);
  }
  if (current.length >= 2) {
    runs.push(current);
  }
  return runs;
}

function appendElevatedDeckShell(
  mesh: MutableStreetMeshData,
  sections: readonly StreetCrossSection[],
  halfWidthM: number,
  surfaceLiftM: number,
  deckDepthM: number,
  terrain: Readonly<TerrainTileData>,
): void {
  const firstVertex = mesh.positions.length / 3;
  for (const section of sections) {
    const minimum = pointAtOffset(section, -halfWidthM);
    const maximum = pointAtOffset(section, halfWidthM);
    const minimumTop = sampleHeight(terrain, minimum.x, minimum.z) +
      surfaceLiftM;
    const maximumTop = sampleHeight(terrain, maximum.x, maximum.z) +
      surfaceLiftM;
    mesh.positions.push(
      minimum.x,
      minimumTop - deckDepthM,
      minimum.z,
      minimum.x,
      minimumTop,
      minimum.z,
      maximum.x,
      maximumTop,
      maximum.z,
      maximum.x,
      maximumTop - deckDepthM,
      maximum.z,
    );
    mesh.uvs.push(
      section.point.distanceM,
      0,
      section.point.distanceM,
      1,
      section.point.distanceM,
      1,
      section.point.distanceM,
      0,
    );
  }
  for (let index = 1; index < sections.length; index += 1) {
    const from = firstVertex + (index - 1) * 4;
    const to = firstVertex + index * 4;
    mesh.indices.push(
      // Left and right fascia.
      from,
      from + 1,
      to + 1,
      from,
      to + 1,
      to,
      from + 3,
      to + 2,
      from + 2,
      from + 3,
      to + 3,
      to + 2,
      // Downward-facing underside.
      from,
      to + 3,
      from + 3,
      from,
      to,
      to + 3,
    );
  }
  const lastVertex = firstVertex + (sections.length - 1) * 4;
  mesh.indices.push(
    firstVertex,
    firstVertex + 3,
    firstVertex + 2,
    firstVertex,
    firstVertex + 2,
    firstVertex + 1,
    lastVertex,
    lastVertex + 2,
    lastVertex + 3,
    lastVertex,
    lastVertex + 1,
    lastVertex + 2,
  );
}

function appendElevatedJunctionShell(
  mesh: MutableStreetMeshData,
  junction: Readonly<StreetJunction>,
  surfaceLiftM: number,
  deckDepthM: number,
  terrain: Readonly<TerrainTileData>,
): void {
  const boundary = deduplicatePolygonPoints(
    createJunctionCorners(junction).flatMap((corner) => corner.inner),
  );
  const triangles = triangulateSimplePolygon(boundary);
  if (boundary.length < 3 || triangles.length === 0) {
    return;
  }
  const firstVertex = mesh.positions.length / 3;
  for (const point of boundary) {
    const top = sampleHeight(terrain, point.x, point.z) + surfaceLiftM;
    mesh.positions.push(
      point.x,
      top - deckDepthM,
      point.z,
      point.x,
      top,
      point.z,
    );
    mesh.uvs.push(point.x, point.z, point.x, point.z);
  }
  for (const [first, second, third] of triangles) {
    mesh.indices.push(
      firstVertex + first * 2,
      firstVertex + second * 2,
      firstVertex + third * 2,
    );
  }
  for (let index = 0; index < boundary.length; index += 1) {
    const next = (index + 1) % boundary.length;
    const fromBottom = firstVertex + index * 2;
    const fromTop = fromBottom + 1;
    const toBottom = firstVertex + next * 2;
    const toTop = toBottom + 1;
    mesh.indices.push(
      fromBottom,
      fromTop,
      toTop,
      fromBottom,
      toTop,
      toBottom,
    );
  }
}

function appendElevatedWalkablePrism(
  vertices: number[],
  indices: number[],
  sections: readonly StreetCrossSection[],
  minimumOffsetM: number,
  maximumOffsetM: number,
  surfaceLiftM: number,
  deckDepthM: number,
  terrain: Readonly<TerrainTileData>,
): void {
  const firstVertex = vertices.length / 3;
  for (const section of sections) {
    const minimum = pointAtOffset(section, minimumOffsetM);
    const maximum = pointAtOffset(section, maximumOffsetM);
    const minimumTop = sampleHeight(terrain, minimum.x, minimum.z) +
      surfaceLiftM;
    const maximumTop = sampleHeight(terrain, maximum.x, maximum.z) +
      surfaceLiftM;
    vertices.push(
      minimum.x,
      minimumTop - deckDepthM,
      minimum.z,
      minimum.x,
      minimumTop,
      minimum.z,
      maximum.x,
      maximumTop,
      maximum.z,
      maximum.x,
      maximumTop - deckDepthM,
      maximum.z,
    );
  }
  for (let index = 1; index < sections.length; index += 1) {
    const from = firstVertex + (index - 1) * 4;
    const to = firstVertex + index * 4;
    indices.push(
      // Walkable road deck.
      from + 1,
      from + 2,
      to + 2,
      from + 1,
      to + 2,
      to + 1,
      // Underside and both fascia walls.
      from,
      to + 3,
      from + 3,
      from,
      to,
      to + 3,
      from,
      from + 1,
      to + 1,
      from,
      to + 1,
      to,
      from + 3,
      to + 2,
      from + 2,
      from + 3,
      to + 3,
      to + 2,
    );
  }
  const lastVertex = firstVertex + (sections.length - 1) * 4;
  indices.push(
    firstVertex,
    firstVertex + 3,
    firstVertex + 2,
    firstVertex,
    firstVertex + 2,
    firstVertex + 1,
    lastVertex,
    lastVertex + 2,
    lastVertex + 3,
    lastVertex,
    lastVertex + 1,
    lastVertex + 2,
  );
}

function appendVariableWalkablePrism(
  vertices: number[],
  indices: number[],
  sections: readonly StreetCrossSection[],
  firstOffsetsM: readonly number[],
  secondOffsetsM: readonly number[],
  topLiftsM: readonly number[],
  terrain: Readonly<TerrainTileData>,
  filter?: StreetSegmentFilter,
): void {
  for (let index = 1; index < sections.length; index += 1) {
    const from = sections[index - 1];
    const to = sections[index];
    const fromFirstOffsetM = firstOffsetsM[index - 1];
    const fromSecondOffsetM = secondOffsetsM[index - 1];
    const toFirstOffsetM = firstOffsetsM[index];
    const toSecondOffsetM = secondOffsetsM[index];
    const fromTopLiftM = topLiftsM[index - 1];
    const toTopLiftM = topLiftsM[index];
    if (
      from === undefined ||
      to === undefined ||
      fromFirstOffsetM === undefined ||
      fromSecondOffsetM === undefined ||
      toFirstOffsetM === undefined ||
      toSecondOffsetM === undefined ||
      fromTopLiftM === undefined ||
      toTopLiftM === undefined
    ) {
      continue;
    }
    const minimumOffsetM = Math.min(
      fromFirstOffsetM,
      fromSecondOffsetM,
      toFirstOffsetM,
      toSecondOffsetM,
    );
    const maximumOffsetM = Math.max(
      fromFirstOffsetM,
      fromSecondOffsetM,
      toFirstOffsetM,
      toSecondOffsetM,
    );
    if (
      filter !== undefined &&
      !filter(from, to, minimumOffsetM, maximumOffsetM)
    ) {
      continue;
    }
    const fromFirst = pointAtOffset(
      from,
      Math.min(fromFirstOffsetM, fromSecondOffsetM),
    );
    const fromSecond = pointAtOffset(
      from,
      Math.max(fromFirstOffsetM, fromSecondOffsetM),
    );
    const toFirst = pointAtOffset(
      to,
      Math.min(toFirstOffsetM, toSecondOffsetM),
    );
    const toSecond = pointAtOffset(
      to,
      Math.max(toFirstOffsetM, toSecondOffsetM),
    );
    const fromFirstTerrain = sampleHeight(
      terrain,
      fromFirst.x,
      fromFirst.z,
    );
    const fromSecondTerrain = sampleHeight(
      terrain,
      fromSecond.x,
      fromSecond.z,
    );
    const toFirstTerrain = sampleHeight(terrain, toFirst.x, toFirst.z);
    const toSecondTerrain = sampleHeight(terrain, toSecond.x, toSecond.z);
    const firstVertex = vertices.length / 3;
    vertices.push(
      fromFirst.x,
      fromFirstTerrain - 0.02,
      fromFirst.z,
      fromFirst.x,
      fromFirstTerrain + fromTopLiftM,
      fromFirst.z,
      fromSecond.x,
      fromSecondTerrain + fromTopLiftM,
      fromSecond.z,
      fromSecond.x,
      fromSecondTerrain - 0.02,
      fromSecond.z,
      toFirst.x,
      toFirstTerrain - 0.02,
      toFirst.z,
      toFirst.x,
      toFirstTerrain + toTopLiftM,
      toFirst.z,
      toSecond.x,
      toSecondTerrain + toTopLiftM,
      toSecond.z,
      toSecond.x,
      toSecondTerrain - 0.02,
      toSecond.z,
    );
    indices.push(
      // Walkable top and buried underside.
      firstVertex + 1,
      firstVertex + 2,
      firstVertex + 6,
      firstVertex + 1,
      firstVertex + 6,
      firstVertex + 5,
      firstVertex,
      firstVertex + 7,
      firstVertex + 3,
      firstVertex,
      firstVertex + 4,
      firstVertex + 7,
      // Long sides.
      firstVertex,
      firstVertex + 1,
      firstVertex + 5,
      firstVertex,
      firstVertex + 5,
      firstVertex + 4,
      firstVertex + 3,
      firstVertex + 6,
      firstVertex + 2,
      firstVertex + 3,
      firstVertex + 7,
      firstVertex + 6,
      // Closed end caps.
      firstVertex,
      firstVertex + 3,
      firstVertex + 2,
      firstVertex,
      firstVertex + 2,
      firstVertex + 1,
      firstVertex + 4,
      firstVertex + 6,
      firstVertex + 7,
      firstVertex + 4,
      firstVertex + 5,
      firstVertex + 6,
    );
  }
}

function appendWalkablePrism(
  vertices: number[],
  indices: number[],
  sections: readonly StreetCrossSection[],
  minimumOffsetM: number,
  maximumOffsetM: number,
  config: Readonly<StreetSurfaceFamilyConfig>,
  terrain: Readonly<TerrainTileData>,
): void {
  const firstVertex = vertices.length / 3;
  for (const section of sections) {
    const minimum = pointAtOffset(section, minimumOffsetM);
    const maximum = pointAtOffset(section, maximumOffsetM);
    const minimumTerrain = sampleHeight(terrain, minimum.x, minimum.z);
    const maximumTerrain = sampleHeight(terrain, maximum.x, maximum.z);
    vertices.push(
      minimum.x,
      minimumTerrain - 0.02,
      minimum.z,
      minimum.x,
      minimumTerrain + config.curbHeightM,
      minimum.z,
      maximum.x,
      maximumTerrain + config.curbHeightM,
      maximum.z,
      maximum.x,
      maximumTerrain - 0.02,
      maximum.z,
    );
  }
  for (let index = 1; index < sections.length; index += 1) {
    const from = firstVertex + (index - 1) * 4;
    const to = firstVertex + index * 4;
    indices.push(
      // Walkable top.
      from + 1,
      from + 2,
      to + 2,
      from + 1,
      to + 2,
      to + 1,
      // Buried underside.
      from,
      to + 3,
      from + 3,
      from,
      to,
      to + 3,
      // Two outer walls.
      from,
      from + 1,
      to + 1,
      from,
      to + 1,
      to,
      from + 3,
      to + 2,
      from + 2,
      from + 3,
      to + 3,
      to + 2,
    );
  }
  const lastVertex = firstVertex + (sections.length - 1) * 4;
  indices.push(
    // Closed start and end caps keep Rapier's trimesh volume unambiguous.
    firstVertex,
    firstVertex + 3,
    firstVertex + 2,
    firstVertex,
    firstVertex + 2,
    firstVertex + 1,
    lastVertex,
    lastVertex + 2,
    lastVertex + 3,
    lastVertex,
    lastVertex + 1,
    lastVertex + 2,
  );
}

function createCrossSections(
  points: readonly Readonly<LocalHorizontal>[],
  startTrimM = 0,
  endTrimM = 0,
  maximumSegmentLengthM = STREET_SURFACE_CONFIG.maximumSegmentLengthM,
): readonly StreetCrossSection[] {
  const fullSampled = resamplePolyline(
    points,
    maximumSegmentLengthM,
  );
  const sampled = trimSampledPolyline(fullSampled, startTrimM, endTrimM);
  return sampled.map((point, index) => {
    const previous = sampled[Math.max(0, index - 1)];
    const next = sampled[Math.min(sampled.length - 1, index + 1)];
    if (previous === undefined || next === undefined) {
      return { point, normalX: 0, normalZ: 1 };
    }
    const deltaX = next.x - previous.x;
    const deltaZ = next.z - previous.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM <= 1e-6) {
      return { point, normalX: 0, normalZ: 1 };
    }
    return {
      point,
      normalX: -deltaZ / lengthM,
      normalZ: deltaX / lengthM,
    };
  });
}

function trimSampledPolyline(
  sampled: readonly SampledStreetPoint[],
  startTrimM: number,
  endTrimM: number,
): readonly SampledStreetPoint[] {
  const last = sampled[sampled.length - 1];
  if (last === undefined) {
    return [];
  }
  const startDistanceM = Math.max(0, startTrimM);
  const endDistanceM = Math.max(
    startDistanceM,
    last.distanceM - Math.max(0, endTrimM),
  );
  if (endDistanceM - startDistanceM <= 0.01) {
    return [];
  }
  const start = sampleResampledPolyline(sampled, startDistanceM);
  const end = sampleResampledPolyline(sampled, endDistanceM);
  if (start === null || end === null) {
    return [];
  }
  return [
    start,
    ...sampled.filter((point) =>
      point.distanceM > startDistanceM + 1e-6 &&
      point.distanceM < endDistanceM - 1e-6
    ),
    end,
  ];
}

function sampleResampledPolyline(
  sampled: readonly SampledStreetPoint[],
  requestedDistanceM: number,
): SampledStreetPoint | null {
  for (let index = 1; index < sampled.length; index += 1) {
    const from = sampled[index - 1];
    const to = sampled[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    if (to.distanceM + 1e-6 < requestedDistanceM) {
      continue;
    }
    const lengthM = to.distanceM - from.distanceM;
    const t = lengthM <= 1e-6
      ? 0
      : Math.max(
        0,
        Math.min(1, (requestedDistanceM - from.distanceM) / lengthM),
      );
    return {
      x: from.x + (to.x - from.x) * t,
      z: from.z + (to.z - from.z) * t,
      distanceM: requestedDistanceM,
    };
  }
  const only = sampled[0];
  return only === undefined
    ? null
    : { x: only.x, z: only.z, distanceM: only.distanceM };
}

function resamplePolyline(
  points: readonly Readonly<LocalHorizontal>[],
  maximumSegmentLengthM: number,
): readonly SampledStreetPoint[] {
  const sampled: SampledStreetPoint[] = [];
  let distanceM = 0;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM <= 0.01) {
      continue;
    }
    if (sampled.length === 0) {
      sampled.push({ x: from.x, z: from.z, distanceM });
    }
    const sectionCount = Math.ceil(lengthM / maximumSegmentLengthM);
    for (let section = 1; section <= sectionCount; section += 1) {
      const t = section / sectionCount;
      sampled.push({
        x: from.x + deltaX * t,
        z: from.z + deltaZ * t,
        distanceM: distanceM + lengthM * t,
      });
    }
    distanceM += lengthM;
  }
  return sampled;
}

function appendTopStrip(
  mesh: MutableStreetMeshData,
  sections: readonly StreetCrossSection[],
  minimumOffsetM: number,
  maximumOffsetM: number,
  liftM: number,
  terrain: Readonly<TerrainTileData>,
  filter?: StreetSegmentFilter,
): void {
  for (let index = 1; index < sections.length; index += 1) {
    const from = sections[index - 1];
    const to = sections[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    if (
      filter !== undefined &&
      !filter(from, to, minimumOffsetM, maximumOffsetM)
    ) {
      continue;
    }
    const corners = [
      pointAtOffset(from, minimumOffsetM),
      pointAtOffset(from, maximumOffsetM),
      pointAtOffset(to, maximumOffsetM),
      pointAtOffset(to, minimumOffsetM),
    ] as const;
    appendTerrainDrapedTriangle(
      mesh,
      [corners[0], corners[1], corners[2]],
      liftM,
      terrain,
    );
    appendTerrainDrapedTriangle(
      mesh,
      [corners[0], corners[2], corners[3]],
      liftM,
      terrain,
    );
  }
}

function appendVariableWidthTopStrip(
  mesh: MutableStreetMeshData,
  sections: readonly StreetCrossSection[],
  halfWidthsM: readonly number[],
  liftM: number,
  terrain: Readonly<TerrainTileData>,
  filter?: StreetSegmentFilter,
): void {
  for (let index = 1; index < sections.length; index += 1) {
    const from = sections[index - 1];
    const to = sections[index];
    const fromHalfWidthM = halfWidthsM[index - 1];
    const toHalfWidthM = halfWidthsM[index];
    if (
      from === undefined ||
      to === undefined ||
      fromHalfWidthM === undefined ||
      toHalfWidthM === undefined
    ) {
      continue;
    }
    const maximumHalfWidthM = Math.max(fromHalfWidthM, toHalfWidthM);
    if (
      filter !== undefined &&
      !filter(from, to, -maximumHalfWidthM, maximumHalfWidthM)
    ) {
      continue;
    }
    const corners = [
      pointAtOffset(from, -fromHalfWidthM),
      pointAtOffset(from, fromHalfWidthM),
      pointAtOffset(to, toHalfWidthM),
      pointAtOffset(to, -toHalfWidthM),
    ] as const;
    appendTerrainDrapedTriangle(
      mesh,
      [corners[0], corners[1], corners[2]],
      liftM,
      terrain,
    );
    appendTerrainDrapedTriangle(
      mesh,
      [corners[0], corners[2], corners[3]],
      liftM,
      terrain,
    );
  }
}

function appendVariableOffsetTopStrip(
  mesh: MutableStreetMeshData,
  sections: readonly StreetCrossSection[],
  firstOffsetsM: readonly number[],
  secondOffsetsM: readonly number[],
  liftsM: readonly number[],
  terrain: Readonly<TerrainTileData>,
  filter?: StreetSegmentFilter,
): void {
  for (let index = 1; index < sections.length; index += 1) {
    const from = sections[index - 1];
    const to = sections[index];
    const fromFirstOffsetM = firstOffsetsM[index - 1];
    const fromSecondOffsetM = secondOffsetsM[index - 1];
    const toFirstOffsetM = firstOffsetsM[index];
    const toSecondOffsetM = secondOffsetsM[index];
    const fromLiftM = liftsM[index - 1];
    const toLiftM = liftsM[index];
    if (
      from === undefined ||
      to === undefined ||
      fromFirstOffsetM === undefined ||
      fromSecondOffsetM === undefined ||
      toFirstOffsetM === undefined ||
      toSecondOffsetM === undefined ||
      fromLiftM === undefined ||
      toLiftM === undefined
    ) {
      continue;
    }
    const minimumOffsetM = Math.min(
      fromFirstOffsetM,
      fromSecondOffsetM,
      toFirstOffsetM,
      toSecondOffsetM,
    );
    const maximumOffsetM = Math.max(
      fromFirstOffsetM,
      fromSecondOffsetM,
      toFirstOffsetM,
      toSecondOffsetM,
    );
    if (
      filter !== undefined &&
      !filter(from, to, minimumOffsetM, maximumOffsetM)
    ) {
      continue;
    }
    const corners = [
      pointAtOffset(from, fromFirstOffsetM),
      pointAtOffset(from, fromSecondOffsetM),
      pointAtOffset(to, toSecondOffsetM),
      pointAtOffset(to, toFirstOffsetM),
    ] as const;
    appendVariableLiftTopTriangle(
      mesh,
      [corners[0], corners[1], corners[2]],
      [fromLiftM, fromLiftM, toLiftM],
      terrain,
    );
    appendVariableLiftTopTriangle(
      mesh,
      [corners[0], corners[2], corners[3]],
      [fromLiftM, toLiftM, toLiftM],
      terrain,
    );
  }
}

function appendPedestrianGatewayAprons(
  mesh: MutableStreetMeshData,
  sections: readonly StreetCrossSection[],
  transition: Readonly<PedestrianRoadTransition>,
  halfWidthM: number,
  curveHalfWidthM: number,
  liftM: number,
  terrain: Readonly<TerrainTileData>,
): void {
  if (sections.length < 2 || halfWidthM <= 0.01) {
    return;
  }
  const maximumDepthM = pedestrianGatewayMaximumDepthM(sections, transition);
  if (maximumDepthM <= 0.01) {
    return;
  }

  for (const endpoint of ["start", "end"] as const) {
    if (!transition[endpoint]) {
      continue;
    }
    const section = endpoint === "start" ? sections[0] : sections.at(-1);
    if (section === undefined) {
      continue;
    }
    const endpointLeft = pointAtOffset(section, -halfWidthM);
    const endpointRight = pointAtOffset(section, halfWidthM);
    appendTopPolygon(
      mesh,
      [
        endpointLeft,
        endpointRight,
        ...pedestrianGatewayRoadwardCurve(
          section,
          endpoint,
          -halfWidthM,
          halfWidthM,
          curveHalfWidthM,
          maximumDepthM,
        ),
      ],
      liftM,
      terrain,
    );
  }
}

function appendPedestrianGatewayRoadBand(
  mesh: MutableStreetMeshData,
  sections: readonly StreetCrossSection[],
  transition: Readonly<PedestrianRoadTransition>,
  minimumOffsetM: number,
  maximumOffsetM: number,
  curveHalfWidthM: number,
  liftM: number,
  terrain: Readonly<TerrainTileData>,
): void {
  if (
    sections.length < 2 ||
    maximumOffsetM - minimumOffsetM <= 0.01 ||
    curveHalfWidthM <= 0.01
  ) {
    return;
  }
  const maximumDepthM = pedestrianGatewayMaximumDepthM(sections, transition);
  if (maximumDepthM <= 0.01) {
    return;
  }
  for (const endpoint of ["start", "end"] as const) {
    if (!transition[endpoint]) {
      continue;
    }
    const section = endpoint === "start" ? sections[0] : sections.at(-1);
    if (section === undefined) {
      continue;
    }
    const directionSign = endpoint === "start" ? 1 : -1;
    const inwardX = section.normalZ * directionSign;
    const inwardZ = -section.normalX * directionSign;
    const roadwardMinimum = pointAtOffset(section, minimumOffsetM);
    const roadwardMaximum = pointAtOffset(section, maximumOffsetM);
    roadwardMinimum.x += inwardX * maximumDepthM;
    roadwardMinimum.z += inwardZ * maximumDepthM;
    roadwardMaximum.x += inwardX * maximumDepthM;
    roadwardMaximum.z += inwardZ * maximumDepthM;
    appendTopPolygon(
      mesh,
      [
        roadwardMinimum,
        roadwardMaximum,
        ...pedestrianGatewayRoadwardCurve(
          section,
          endpoint,
          minimumOffsetM,
          maximumOffsetM,
          curveHalfWidthM,
          maximumDepthM,
        ),
      ],
      liftM,
      terrain,
    );
  }
}

function pedestrianGatewayRoadwardCurve(
  section: Readonly<StreetCrossSection>,
  endpoint: "start" | "end",
  minimumOffsetM: number,
  maximumOffsetM: number,
  curveHalfWidthM: number,
  maximumDepthM: number,
): readonly LocalHorizontal[] {
  const directionSign = endpoint === "start" ? 1 : -1;
  const inwardX = section.normalZ * directionSign;
  const inwardZ = -section.normalX * directionSign;
  const centreDepthM = Math.min(
    STREET_SURFACE_CONFIG.pedestrianGatewayCentreDepthM,
    maximumDepthM,
  );
  const curveSegments = Math.max(
    2,
    Math.round(
      STREET_SURFACE_CONFIG.pedestrianGatewayCurveSegments *
        (maximumOffsetM - minimumOffsetM) /
        Math.max(0.01, curveHalfWidthM * 2),
    ),
  );
  const curve: LocalHorizontal[] = [];
  for (let index = curveSegments; index >= 0; index -= 1) {
    const offsetM = minimumOffsetM +
      (maximumOffsetM - minimumOffsetM) * (index / curveSegments);
    const lateral = Math.min(1, Math.abs(offsetM) / curveHalfWidthM);
    const easedLateral = lateral * lateral * (3 - 2 * lateral);
    const depthM = centreDepthM +
      (maximumDepthM - centreDepthM) * easedLateral;
    curve.push({
      x: section.point.x + section.normalX * offsetM + inwardX * depthM,
      z: section.point.z + section.normalZ * offsetM + inwardZ * depthM,
    });
  }
  return curve;
}

function appendVariableLiftTopTriangle(
  mesh: MutableStreetMeshData,
  points: readonly [
    Readonly<LocalHorizontal>,
    Readonly<LocalHorizontal>,
    Readonly<LocalHorizontal>,
  ],
  liftsM: readonly [number, number, number],
  terrain: Readonly<TerrainTileData>,
): void {
  if (Math.abs(signedTriangleArea(points[0], points[1], points[2])) <= 1e-8) {
    return;
  }
  const vertexOffset = mesh.positions.length / 3;
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    const liftM = liftsM[index];
    if (point === undefined || liftM === undefined) {
      continue;
    }
    mesh.positions.push(
      point.x,
      sampleHeight(terrain, point.x, point.z) + liftM,
      point.z,
    );
    mesh.uvs.push(point.x, point.z);
  }
  if (signedTriangleArea(points[0], points[1], points[2]) <= 0) {
    mesh.indices.push(vertexOffset, vertexOffset + 1, vertexOffset + 2);
  } else {
    mesh.indices.push(vertexOffset, vertexOffset + 2, vertexOffset + 1);
  }
}

function appendVariableVerticalStrip(
  mesh: MutableStreetMeshData,
  sections: readonly StreetCrossSection[],
  offsetsM: readonly number[],
  lowerLiftsM: readonly number[],
  upperLiftsM: readonly number[],
  terrain: Readonly<TerrainTileData>,
  filter?: StreetSegmentFilter,
): void {
  for (let index = 1; index < sections.length; index += 1) {
    const from = sections[index - 1];
    const to = sections[index];
    const fromOffsetM = offsetsM[index - 1];
    const toOffsetM = offsetsM[index];
    const fromLowerLiftM = lowerLiftsM[index - 1];
    const toLowerLiftM = lowerLiftsM[index];
    const fromUpperLiftM = upperLiftsM[index - 1];
    const toUpperLiftM = upperLiftsM[index];
    if (
      from === undefined ||
      to === undefined ||
      fromOffsetM === undefined ||
      toOffsetM === undefined ||
      fromLowerLiftM === undefined ||
      toLowerLiftM === undefined ||
      fromUpperLiftM === undefined ||
      toUpperLiftM === undefined
    ) {
      continue;
    }
    if (
      filter !== undefined &&
      !filter(
        from,
        to,
        Math.min(fromOffsetM, toOffsetM),
        Math.max(fromOffsetM, toOffsetM),
      )
    ) {
      continue;
    }
    const fromPoint = pointAtOffset(from, fromOffsetM);
    const toPoint = pointAtOffset(to, toOffsetM);
    const fromHeight = sampleHeight(
      terrain,
      fromPoint.x,
      fromPoint.z,
    );
    const toHeight = sampleHeight(terrain, toPoint.x, toPoint.z);
    const vertexOffset = mesh.positions.length / 3;
    mesh.positions.push(
      fromPoint.x,
      fromHeight + fromLowerLiftM,
      fromPoint.z,
      fromPoint.x,
      fromHeight + fromUpperLiftM,
      fromPoint.z,
      toPoint.x,
      toHeight + toUpperLiftM,
      toPoint.z,
      toPoint.x,
      toHeight + toLowerLiftM,
      toPoint.z,
    );
    mesh.uvs.push(
      from.point.distanceM,
      fromLowerLiftM,
      from.point.distanceM,
      fromUpperLiftM,
      to.point.distanceM,
      toUpperLiftM,
      to.point.distanceM,
      toLowerLiftM,
    );
    mesh.indices.push(
      vertexOffset,
      vertexOffset + 1,
      vertexOffset + 2,
      vertexOffset,
      vertexOffset + 2,
      vertexOffset + 3,
    );
  }
}

function appendVerticalStrip(
  mesh: MutableStreetMeshData,
  sections: readonly StreetCrossSection[],
  offsetM: number,
  lowerLiftM: number,
  upperLiftM: number,
  terrain: Readonly<TerrainTileData>,
  filter?: StreetSegmentFilter,
): void {
  for (let index = 1; index < sections.length; index += 1) {
    const from = sections[index - 1];
    const to = sections[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    if (
      filter !== undefined &&
      !filter(from, to, offsetM, offsetM)
    ) {
      continue;
    }
    const fromPoint = pointAtOffset(from, offsetM);
    const toPoint = pointAtOffset(to, offsetM);
    const fromHeight = sampleHeight(terrain, fromPoint.x, fromPoint.z);
    const toHeight = sampleHeight(terrain, toPoint.x, toPoint.z);
    const vertexOffset = mesh.positions.length / 3;
    mesh.positions.push(
      fromPoint.x,
      fromHeight + lowerLiftM,
      fromPoint.z,
      fromPoint.x,
      fromHeight + upperLiftM,
      fromPoint.z,
      toPoint.x,
      toHeight + upperLiftM,
      toPoint.z,
      toPoint.x,
      toHeight + lowerLiftM,
      toPoint.z,
    );
    mesh.uvs.push(
      from.point.distanceM,
      lowerLiftM,
      from.point.distanceM,
      upperLiftM,
      to.point.distanceM,
      upperLiftM,
      to.point.distanceM,
      lowerLiftM,
    );
    mesh.indices.push(
      vertexOffset,
      vertexOffset + 1,
      vertexOffset + 2,
      vertexOffset,
      vertexOffset + 2,
      vertexOffset + 3,
    );
  }
}

function pointToSegmentDistanceM(
  point: Readonly<LocalHorizontal>,
  from: Readonly<LocalHorizontal>,
  to: Readonly<LocalHorizontal>,
): number {
  const deltaX = to.x - from.x;
  const deltaZ = to.z - from.z;
  const lengthSquaredM = deltaX * deltaX + deltaZ * deltaZ;
  if (lengthSquaredM <= 1e-8) {
    return Math.hypot(point.x - from.x, point.z - from.z);
  }
  const t = Math.max(
    0,
    Math.min(
      1,
      (
        (point.x - from.x) * deltaX +
        (point.z - from.z) * deltaZ
      ) / lengthSquaredM,
    ),
  );
  return Math.hypot(
    point.x - (from.x + deltaX * t),
    point.z - (from.z + deltaZ * t),
  );
}

function pointAtOffset(
  section: Readonly<StreetCrossSection>,
  offsetM: number,
): LocalHorizontal {
  return {
    x: section.point.x + section.normalX * offsetM,
    z: section.point.z + section.normalZ * offsetM,
  };
}

function sampleHeight(
  terrain: Readonly<TerrainTileData>,
  x: number,
  z: number,
): number {
  const maximum = terrain.spacingM * (terrain.samplesPerSide - 1);
  const clampedX = Math.min(maximum, Math.max(0, x));
  const clampedZ = Math.min(maximum, Math.max(0, z));
  const sampleAt = (sampleX: number, sampleZ: number): number => {
    const height = sampleTerrainTileData(
      terrain,
      { x: 0, z: 0 },
      { x: sampleX, z: sampleZ },
    );
    if (height === null) {
      throw new Error(
        `Could not sample street terrain at (${sampleX}, ${sampleZ})`,
      );
    }
    return height;
  };
  let height = sampleAt(clampedX, clampedZ);
  if (x < 0) {
    height += (
      sampleAt(0, clampedZ) -
      sampleAt(terrain.spacingM, clampedZ)
    ) * (-x / terrain.spacingM);
  } else if (x > maximum) {
    height += (
      sampleAt(maximum, clampedZ) -
      sampleAt(maximum - terrain.spacingM, clampedZ)
    ) * ((x - maximum) / terrain.spacingM);
  }
  if (z < 0) {
    height += (
      sampleAt(clampedX, 0) -
      sampleAt(clampedX, terrain.spacingM)
    ) * (-z / terrain.spacingM);
  } else if (z > maximum) {
    height += (
      sampleAt(clampedX, maximum) -
      sampleAt(clampedX, maximum - terrain.spacingM)
    ) * ((z - maximum) / terrain.spacingM);
  }
  return height;
}

function freezeMesh(
  mesh: Readonly<MutableStreetMeshData>,
): StreetMeshData | null {
  if (mesh.positions.length === 0) {
    return null;
  }
  return {
    positions: mesh.positions,
    uvs: mesh.uvs,
    indices: mesh.indices,
  };
}

function freezeRoadSurfaceMeshes(
  meshes: Readonly<Record<RoadSurfaceClass, MutableStreetMeshData>>,
): Readonly<Record<RoadSurfaceClass, StreetMeshData | null>> {
  return Object.fromEntries(
    ROAD_SURFACE_CLASSES.map((surfaceClass) => [
      surfaceClass,
      freezeMesh(meshes[surfaceClass]),
    ]),
  ) as Record<RoadSurfaceClass, StreetMeshData | null>;
}

function freezePedestrianSurfaceMeshes(
  meshes: Readonly<
    Record<PedestrianSurfaceClass, MutableStreetMeshData>
  >,
): Readonly<Record<PedestrianSurfaceClass, StreetMeshData | null>> {
  return Object.fromEntries(
    PEDESTRIAN_SURFACE_CLASSES.map((surfaceClass) => [
      surfaceClass,
      freezeMesh(meshes[surfaceClass]),
    ]),
  ) as Record<PedestrianSurfaceClass, StreetMeshData | null>;
}
